import { Routes, Route, NavLink, Navigate } from 'react-router-dom';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, signInAnonymously, onAuthStateChanged, GoogleAuthProvider, signOut, signInWithPopup } from 'firebase/auth';
import { getVertexAI, getGenerativeModel } from "firebase/vertexai-preview";
import { getFirestore, collection, doc, addDoc, onSnapshot, query, where, updateDoc, deleteDoc, writeBatch, getDocs, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { firebaseConfig } from './firebaseConfig';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

marked.setOptions({
    breaks: true, // This tells marked to treat single line breaks as <br> tags
    gfm: true,    // Use GitHub Flavored Markdown for better compatibility
});

// --- Helper Functions & Constants ---
const appId = 'stackd-app';
const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
const parseDateStringAsLocal = (dateString) => {
    if (!dateString) return new Date();
    // Handles "YYYY-MM-DD" by splitting and creating a date in the local timezone
    // to prevent the date from shifting due to UTC conversion.
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
};

// --- Icons (Using currentColor for easy styling) ---
const HomeIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg>;
const BudgetIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="14" x="2" y="5" rx="2" /><line x1="2" x2="22" y1="10" y2="10" /></svg>;
const BillsIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" /><polyline points="14 2 14 8 20 8" /><line x1="16" x2="8" y1="13" y2="13" /><line x1="16" x2="8" y1="17" y2="17" /><line x1="10" x2="8" y1="9" y2="9" /></svg>;
const TransactionsIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" /><polyline points="14 2 14 8 20 8" /><line x1="16" x2="8" y1="13" y2="13" /><line x1="16" x2="8" y1="17" y2="17" /><line x1="10" x2="8" y1="9" y2="9" /></svg>;
const SparklesIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" /><path d="M18 9h.01" /><path d="M15 6h.01" /><path d="M21 12h.01" /><path d="M12 18v.01" /><path d="M9 15v.01" /><path d="M6 12v.01" /><path d="M3 9v.01" /></svg>;
const SavingsIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3.5 9.5a4.5 4.5 0 1 0 0-5 4.5 4.5 0 0 0 0 5Z" /><path d="M4 9h16" /><path d="M4 15h16" /><path d="M10 9v10" /><path d="M16 9v10" /><path d="M4 19h16" /></svg>;
const DebtIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><line x1="2" x2="22" y1="12" y2="12" /><path d="M12 2a10 10 0 0 0-10 10v1a10 10 0 0 0 10 10" /><path d="M12 22a10 10 0 0 0 10-10v-1a10 10 0 0 0-10-10" /></svg>;
const LearnIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20" /></svg>;
const SettingsIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 0 2l-.15.08a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l-.22-.38a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1 0-2l.15.08a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" /><circle cx="12" cy="12" r="3" /></svg>;
const CalendarIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="18" x="3" y="4" rx="2" ry="2" /><line x1="16" x2="16" y1="2" y2="6" /><line x1="8" x2="8" y1="2" y2="6" /><line x1="3" x2="21" y1="10" y2="10" /></svg>;
const PlusCircleIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" x2="12" y1="8" y2="16" /><line x1="8" x2="16" y1="12" y2="12" /></svg>;
const EditIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>;
const ChartIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18" /><path d="m19 9-5 5-4-4-3 3" /></svg>;
const TrashIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><line x1="10" x2="10" y1="11" y2="17" /><line x1="14" x2="14" y1="11" y2="17" /></svg>;
const GripVerticalIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="12" r="1" /><circle cx="9" cy="5" r="1" /><circle cx="9" cy="19" r="1" /><circle cx="15" cy="12" r="1" /><circle cx="15" cy="5" r="1" /><circle cx="15" cy="19" r="1" /></svg>;
const GoogleIcon = () => <svg viewBox="0 0 48 48" className="w-6 h-6"><path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C12.955 4 4 12.955 4 24s8.955 20 20 20s20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"></path><path fill="#FF3D00" d="M6.306 14.691l6.057 4.711C15.006 15.122 19.227 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C16.318 4 9.656 8.337 6.306 14.691z"></path><path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.222 0-9.618-3.217-11.283-7.614l-6.062 4.721C9.034 39.754 15.999 44 24 44z"></path><path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571l6.19 5.238C42.032 35.622 44 30.138 44 24c0-1.341-.138-2.65-.389-3.917z"></path></svg>;
const LogoutIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" x2="9" y1="12" y2="12" /></svg>;
const GuestIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>;
const StackIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"></path></svg>;
const MenuIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="12" x2="21" y2="12"></line><line x1="3" y1="6" x2="21" y2="6"></line><line x1="3" y1="18" x2="21" y2="18"></line></svg>;
const XIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>;


// --- Styled Components (as functions for reusability) ---
const PrimaryButton = ({ children, onClick, disabled, type = 'button', className = '' }) => (
    <button
        type={type}
        onClick={onClick}
        disabled={disabled}
        className={`bg-stackd-green text-black font-medium uppercase tracking-wider text-sm py-2.5 px-5 rounded-lg transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${className}`}
    >
        {children}
    </button>
);

const SecondaryButton = ({ children, onClick, disabled, type = 'button', className = '' }) => (
    <button
        type={type}
        onClick={onClick}
        disabled={disabled}
        className={`bg-transparent border border-secondary-text text-secondary-text hover:border-primary-text hover:text-primary-text font-medium uppercase tracking-wider text-sm py-2.5 px-5 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${className}`}
    >
        {children}
    </button>
);

const AiButton = ({ children, onClick, disabled, className = '' }) => (
    <button
        onClick={onClick}
        disabled={disabled}
        className={`relative inline-flex items-center justify-center gap-2 px-5 py-2.5 overflow-hidden font-medium uppercase tracking-wider text-stackd-green transition-all duration-300 rounded-lg group bg-dark-gray border border-stackd-green/50 hover:border-stackd-green ${className}`}
    >
        <span className="absolute top-0 right-full w-full h-full bg-gradient-to-r from-transparent via-stackd-green/50 to-transparent transform group-hover:translate-x-full transition-transform duration-1000 animate-shimmer"></span>
        <StackIcon /> {/* <-- REPLACE IT WITH THIS LINE */}
        <span className="relative">{children}</span>
    </button>
);

const EmptyState = ({ icon, title, message, buttonText, onButtonClick }) => (
    <Card className="text-center p-10 mt-6 flex flex-col items-center border-2 border-dashed border-border-color bg-dark-gray/50">
        <div className="text-secondary-text mb-4">
            {React.cloneElement(icon, { className: "w-12 h-12" })}
        </div>
        <h2 className="text-2xl font-bold text-primary-text mb-2">{title}</h2>
        <p className="text-secondary-text mb-6 max-w-sm">{message}</p>
        <PrimaryButton onClick={onButtonClick}>
            <PlusCircleIcon /> {buttonText}
        </PrimaryButton>
    </Card>
);

const Card = ({ children, className = '' }) => (
    <div className={`bg-dark-gray border border-border-color rounded-lg ${className}`}>
        {children}
    </div>
);

const CurrencyInput = ({ value, onChange, placeholder = "0.00", className = '', ...props }) => (
    <div className={`relative ${className}`}>
        <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-secondary-text">$</span>
        <input
            type="number"
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            className="form-input pl-7 text-right"
            min="0"
            step="0.01"
            {...props}
        />
    </div>
);

const PercentageInput = ({ value, onChange, placeholder, className = '', ...props }) => (
    <div className={`relative ${className}`}>
        <input
            type="number"
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            className="form-input pr-7 text-right"
            min="0"
            step="0.01"
            {...props}
        />
        <span className="absolute inset-y-0 right-0 pr-3 flex items-center text-secondary-text">%</span>
    </div>
);

const MobileHeader = ({ onMenuClick }) => (
    <header className="lg:hidden p-4 bg-dark-gray/80 backdrop-blur-sm sticky top-0 z-10 flex items-center justify-between border-b border-border-color mb-4">
        <h1 className="text-xl font-bold uppercase tracking-tighter">STACKD</h1>
        <button onClick={onMenuClick} className="p-2">
            <MenuIcon />
        </button>
    </header>
);

// --- Layout Components ---
const Sidebar = ({ user, onSignUp, onSignOut, isSidebarOpen, setIsSidebarOpen }) => (
    <aside className={`bg-dark-gray flex flex-col justify-between border-r border-border-color h-full fixed top-0 left-0 w-64 p-6 z-30 transform transition-transform duration-300 ease-in-out ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:w-64 lg:translate-x-0 lg:static lg:z-auto`}>
        <div>
            <div className="flex items-center justify-between lg:justify-start mb-12">
                <h1 className="text-3xl font-bold text-primary-text tracking-tighter uppercase">STACKD</h1>
                <button onClick={() => setIsSidebarOpen(false)} className="p-2 lg:hidden"><XIcon /></button>
            </div>
            <div className="space-y-2">
                {/* The setIsSidebarOpen prop is now correctly passed to each NavItem */}
                <NavItem icon={<HomeIcon />} label="Dashboard" to="/app/dashboard" setIsSidebarOpen={setIsSidebarOpen} />
                <NavItem icon={<BudgetIcon />} label="Budget" to="/app/budget" setIsSidebarOpen={setIsSidebarOpen} />
                <NavItem icon={<TransactionsIcon />} label="Transactions" to="/app/transactions" setIsSidebarOpen={setIsSidebarOpen} />
                <NavItem icon={<BillsIcon />} label="Bills" to="/app/bills" setIsSidebarOpen={setIsSidebarOpen} />
                <NavItem icon={<DebtIcon />} label="Debt" to="/app/debt" setIsSidebarOpen={setIsSidebarOpen} />
                <NavItem icon={<SavingsIcon />} label="Goals" to="/app/savings" setIsSidebarOpen={setIsSidebarOpen} />
                <NavItem icon={<ChartIcon />} label="Reporting" to="/app/reporting" setIsSidebarOpen={setIsSidebarOpen} />
                <NavItem icon={<CalendarIcon />} label="Calendar" to="/app/calendar" setIsSidebarOpen={setIsSidebarOpen} />
                <NavItem icon={<LearnIcon />} label="Learn" to="/app/learn" setIsSidebarOpen={setIsSidebarOpen} />
                <NavItem icon={<SettingsIcon />} label="Settings" to="/app/settings" setIsSidebarOpen={setIsSidebarOpen} />
            </div>
        </div>
        <div className="space-y-4">
            <UserProfile user={user} onSignUp={onSignUp} onSignOut={onSignOut} setIsSidebarOpen={setIsSidebarOpen} />
        </div>
    </aside>
);

const NavItem = ({ icon, label, to, setIsSidebarOpen }) => (
    <NavLink
        to={to}
        onClick={() => setIsSidebarOpen(false)} // This now correctly closes the sidebar
        className={({ isActive }) =>
            `w-full flex items-center p-3 rounded-lg transition-colors duration-200 ${isActive ? 'bg-off-black text-stackd-green' : 'text-secondary-text hover:bg-off-black/50 hover:text-primary-text'}`
        }
    >
        {icon}
        <span className="ml-4 font-semibold lg:block">{label}</span>
    </NavLink>
);

const UserProfile = ({ user, onSignUp, onSignOut, setIsSidebarOpen }) => {
    if (!user) return null;
    return (
        <Card className="p-2 bg-off-black">
            {user.isAnonymous ? (
                // This component now also receives setIsSidebarOpen
                <button onClick={() => { onSignUp(); setIsSidebarOpen(false); }} className="w-full flex items-center p-2 rounded-lg transition-colors text-secondary-text hover:bg-dark-gray hover:text-stackd-green">
                    <GuestIcon />
                    <div className="ml-3 text-left hidden lg:block">
                        <p className="font-semibold text-sm">Guest Mode</p>
                        <p className="text-xs text-secondary-text">Click to Sign Up</p>
                    </div>
                </button>
            ) : (
                <div className="w-full flex items-center p-2">
                    <img src={user.photoURL || `https://api.dicebear.com/8.x/initials/svg?seed=${user.displayName}`} alt="User Avatar" className="w-10 h-10 rounded-full" />
                    <div className="ml-3 flex-1 text-left hidden lg:block overflow-hidden">
                        <p className="font-semibold text-sm text-primary-text truncate">{user.displayName}</p>
                        <p className="text-xs text-secondary-text truncate">{user.email}</p>
                    </div>
                    <button onClick={onSignOut} className="ml-2 p-2 text-secondary-text hover:text-error hidden lg:block">
                        <LogoutIcon />
                    </button>
                </div>
            )}
        </Card>
    );
};

// --- Main App Component ---
export default function App() {
    const [db, setDb] = useState(null);
    const [auth, setAuth] = useState(null);
    const [geminiModel, setGeminiModel] = useState(null);
    const [user, setUser] = useState(null);
    const [isAuthReady, setIsAuthReady] = useState(false);
    const [paychecks, setPaychecks] = useState([]);
    const [categories, setCategories] = useState([]);
    const [budgetItems, setBudgetItems] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [bills, setBills] = useState([]);
    const [savingsGoals, setSavingsGoals] = useState([]);
    const [savingsTransactions, setSavingsTransactions] = useState([]);
    const [debtGoals, setDebtGoals] = useState([]);
    const [debtPayments, setDebtPayments] = useState([]);
    const [settings, setSettings] = useState({ paycheckFrequency: 'manual', nextPaycheckDate: '' });
    const [selectedPaycheckId, setSelectedPaycheckId] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [modal, setModal] = useState(null);
    const [authModalOpen, setAuthModalOpen] = useState(false);
    const [authError, setAuthError] = useState('');
    const [isPremium, setIsPremium] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [onboarding, setOnboarding] = useState({ inProgress: false, step: 0 });
    const [showOnboarding, setShowOnboarding] = useState(false);

    const setupNewUser = async (user, db, setOnboardingStep) => {
        try {
            const batch = writeBatch(db);
            let order = 0;
            // 1. Default Budget Categories
            const defaultCategories = ['Savings', 'Debt Payments', 'Groceries', 'Utilities', 'Transportation', 'Entertainment', 'Personal Care', 'Miscellaneous'];
            defaultCategories.forEach(name => {
                const categoryRef = doc(collection(db, `/artifacts/${appId}/users/${user.uid}/categories`));
                batch.set(categoryRef, { name, userId: user.uid, order: order++ });
            });

            // 2. Default Savings Goal
            const targetDate = new Date();
            targetDate.setFullYear(targetDate.getFullYear() + 1); // Set target for one year from now
            const goalRef = doc(collection(db, `/artifacts/${appId}/users/${user.uid}/savingsGoals`));
            batch.set(goalRef, {
                name: "Emergency Fund",
                goalAmount: 1000,
                targetDate: targetDate,
                userId: user.uid,
                order: 0
            });

            // 3. Create the initial settings document
            const settingsRef = doc(db, `/artifacts/${appId}/users/${user.uid}/settings/main`);
            batch.set(settingsRef, {
                defaultsCreated: true,
                paycheckFrequency: 'manual',
                nextPaycheckDate: '',
                defaultPaycheckAmount: 0,
                premium: false
            });

            await batch.commit();
            console.log("Default data created for new user:", user.uid);
            setOnboarding({ inProgress: true, step: 0 });
            setShowOnboarding(true);
        } catch (error) {
            console.error("Error setting up new user:", error);
        }
    };

    // --- Firebase Initialization and Auth ---
    useEffect(() => {
        if (!firebaseConfig || Object.keys(firebaseConfig).length === 0) {
            console.error("Firebase config is missing.");
            setIsAuthReady(true);
            return;
        }
        try {
            const app = initializeApp(firebaseConfig);
            const firestoreDb = getFirestore(app);
            const firebaseAuth = getAuth(app);

            try {
                const vertex = getVertexAI(app);
                const model = getGenerativeModel(vertex, { model: "gemini-2.0-flash-lite-001" });
                setGeminiModel(model);
                console.log("Gemini Pro model initialized.");
            } catch (aiError) {
                console.error("Vertex AI Initialization Error:", aiError);
            }

            setDb(firestoreDb);
            setAuth(firebaseAuth);

            const unsubscribe = onAuthStateChanged(firebaseAuth, async (user) => {
                if (user) {
                    setUser(user);
                    if (!user.isAnonymous) {
                        const settingsRef = doc(firestoreDb, `/artifacts/${appId}/users/${user.uid}/settings/main`);
                        const docSnap = await getDoc(settingsRef);
                        if (!docSnap.exists()) {
                            await setupNewUser(user, firestoreDb, setOnboarding, setShowOnboarding);
                        }
                    }
                } else {
                    await signInAnonymously(firebaseAuth);
                }
                setIsAuthReady(true);
            });

            return () => unsubscribe();
        } catch (error) {
            console.error("Firebase Initialization Error:", error);
            setIsAuthReady(true);
        }
    }, []);

    // --- Data Fetching from Firestore ---
    useEffect(() => {
        if (!isAuthReady || !db || !user) return;
        const fetchData = (collectionName, setState, orderByField = null) => {
            const collRef = collection(db, `/artifacts/${appId}/users/${user.uid}/${collectionName}`);
            const q = query(collRef, where("userId", "==", user.uid));
            const unsubscribe = onSnapshot(q, (snapshot) => {
                let data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

                // Sort by the 'order' field first, then by the specified orderByField as a fallback.
                data.sort((a, b) => {
                    const orderA = a.order ?? Infinity;
                    const orderB = b.order ?? Infinity;
                    if (orderA !== orderB) {
                        return orderA - orderB;
                    }
                    if (orderByField && b[orderByField] && a[orderByField]) {
                        // For date fields, descending order is newest first
                        return (b[orderByField].seconds || 0) - (a[orderByField].seconds || 0);
                    }
                    // Fallback for non-date fields if needed
                    if (a.name && b.name) {
                        return a.name.localeCompare(b.name);
                    }
                    return 0;
                });

                setState(data);
            }, (error) => console.error(`Error fetching ${collectionName}:`, error));
            return unsubscribe;
        };
        const unsubSettings = onSnapshot(doc(db, `/artifacts/${appId}/users/${user.uid}/settings/main`), (doc) => {
            if (doc.exists()) {
                setSettings(doc.data());
                setIsPremium(doc.data()?.premium || false);
            }
        });
        const unsubPaychecks = fetchData('paychecks', setPaychecks, 'date');
        const unsubCategories = fetchData('categories', setCategories);
        const unsubBudgetItems = fetchData('budgetItems', setBudgetItems);
        const unsubTransactions = fetchData('transactions', setTransactions, 'date');
        const unsubBills = fetchData('monthlyBills', setBills);
        const unsubSavingsGoals = fetchData('savingsGoals', setSavingsGoals);
        const unsubSavingsTransactions = fetchData('savingsTransactions', setSavingsTransactions);
        const unsubDebtGoals = fetchData('debtGoals', setDebtGoals);
        const unsubDebtPayments = fetchData('debtPayments', setDebtPayments);
        setIsLoading(false);
        return () => { unsubSettings(); unsubPaychecks(); unsubCategories(); unsubBudgetItems(); unsubTransactions(); unsubBills(); unsubSavingsGoals(); unsubSavingsTransactions(); unsubDebtGoals(); unsubDebtPayments(); };
    }, [isAuthReady, db, user]);

    // --- Authentication Handlers ---
    const handleGoogleSignIn = async () => {
        if (!auth) return;
        const provider = new GoogleAuthProvider();
        try {
            setAuthError('');
            await signInWithPopup(auth, provider);
            // The onAuthStateChanged listener will automatically update the app state.
            setAuthModalOpen(false);
        } catch (error) {
            console.error("Error during Google sign-in:", error);
            setAuthError("An error occurred during sign-in. Please try again.");
        }
    };
    const handleSignOut = async () => { if (!auth) return; await signOut(auth); setUser(null); window.location.reload(); };

    // --- Auto-select latest paycheck ---
    useEffect(() => { if (paychecks.length > 0 && !selectedPaycheckId) { const sortedPaychecks = [...paychecks].sort((a, b) => (b.date?.toDate() ?? 0) - (a.date?.toDate() ?? 0)); if (sortedPaychecks.length > 0) setSelectedPaycheckId(sortedPaychecks[0].id); } }, [paychecks, selectedPaycheckId]);

    // --- Memoized Calculations ---
    const sortedPaychecks = useMemo(() => [...paychecks].sort((a, b) => (b.date?.toDate() ?? 0) - (a.date?.toDate() ?? 0)), [paychecks]);

    const budgetData = useMemo(() => {
        if (!selectedPaycheckId) return { paycheckTotal: 0, totalAllocated: 0, totalSpent: 0, unallocated: 0, categoryDetails: [] };
        const currentPaycheck = paychecks.find(p => p.id === selectedPaycheckId);
        const paycheckTotal = currentPaycheck?.amount || 0;
        const relevantBudgetItems = budgetItems.filter(bi => bi.paycheckId === selectedPaycheckId);
        const totalAllocated = relevantBudgetItems.reduce((sum, bi) => sum + (bi.allocated || 0), 0);
        const categoryDetails = categories.map(category => {
            const budgetItem = relevantBudgetItems.find(bi => bi.categoryId === category.id);
            const allocated = budgetItem?.allocated || 0;
            const relevantTransactions = transactions.filter(t => t.paycheckId === selectedPaycheckId && t.categoryId === category.id);
            const spent = relevantTransactions.reduce((sum, t) => sum + (t.amount || 0), 0);
            return { ...category, budgetItemId: budgetItem?.id, allocated, spent, remaining: allocated - spent, transactions: relevantTransactions.sort((a, b) => (b.date?.seconds || 0) - (a.date?.seconds || 0)) };
        });
        const totalSpent = categoryDetails.reduce((sum, cat) => sum + (cat.spent || 0), 0);
        return { paycheckTotal, totalAllocated, totalSpent, unallocated: paycheckTotal - totalAllocated, categoryDetails: categoryDetails }; // Removed sort, will rely on DnD order
    }, [selectedPaycheckId, paychecks, categories, budgetItems, transactions]);

    // --- Firestore Write Handlers ---
    const handleAdd = async (collectionName, data, closeModal = true) => {
        if (!db || !user) return;
        try {
            // Get the highest order number to add the new item at the end
            const collRef = collection(db, `/artifacts/${appId}/users/${user.uid}/${collectionName}`);
            const q = query(collRef);
            const snapshot = await getDocs(q);
            const maxOrder = snapshot.docs.reduce((max, doc) => Math.max(max, doc.data().order || 0), -1);

            const docRef = await addDoc(collRef, { ...data, userId: user.uid, order: maxOrder + 1 });
            if (closeModal) setModal(null);
            return docRef;
        } catch (error) {
            console.error(`Error adding to ${collectionName}:`, error);
        }
    };
    const handleUpdate = async (collectionName, docId, data) => { if (!db || !user) return; try { await updateDoc(doc(db, `/artifacts/${appId}/users/${user.uid}/${collectionName}`, docId), data); setModal(null); } catch (error) { console.error(`Error updating ${collectionName}:`, error); } };
    const handleDelete = async (collectionName, docId) => { if (!db || !user || !docId) return; if (await showConfirm("Are you sure you want to delete this? This action cannot be undone.")) { try { await deleteDoc(doc(db, `/artifacts/${appId}/users/${user.uid}/${collectionName}`, docId)); } catch (error) { console.error(`Error deleting from ${collectionName}:`, error); } } };
    const handleDeletePaycheck = async (paycheckId) => { if (!db || !user || !paycheckId) return; if (await showConfirm("Are you sure? This will delete the paycheck and all associated budget items and transactions.")) { const batch = writeBatch(db); batch.delete(doc(db, `/artifacts/${appId}/users/${user.uid}/paychecks`, paycheckId)); const itemsQuery = query(collection(db, `/artifacts/${appId}/users/${user.uid}/budgetItems`), where("paycheckId", "==", paycheckId)); const transQuery = query(collection(db, `/artifacts/${appId}/users/${user.uid}/transactions`), where("paycheckId", "==", paycheckId)); const [itemsSnap, transSnap] = await Promise.all([getDocs(itemsQuery), getDocs(transQuery)]); itemsSnap.forEach(doc => batch.delete(doc.ref)); transSnap.forEach(doc => batch.delete(doc.ref)); await batch.commit(); setSelectedPaycheckId(''); } };
    /* const handleAddPaycheckAndProcessRecurringItems = async (paycheckData) => {
        if (!db || !user) return;
        const newPaycheckRef = await handleAdd('paychecks', paycheckData, false);
        if (!newPaycheckRef) { setModal(null); return; }
        if (settings.paycheckFrequency === 'manual') { setModal(null); return; }
        const newPaycheckDate = paycheckData.date;
        const localSortedPaychecks = [...paychecks, { id: newPaycheckRef.id, ...paycheckData }].sort((a, b) => (a.date?.toDate ? a.date.toDate() : a.date) - (b.date?.toDate ? b.date.toDate() : b.date));
        const newPaycheckIndex = localSortedPaychecks.findIndex(p => p.id === newPaycheckRef.id);
        const previousPaycheck = newPaycheckIndex > 0 ? localSortedPaychecks[newPaycheckIndex - 1] : null;
        const periodStartDate = previousPaycheck ? new Date(previousPaycheck.date.toDate().getTime() + 86400000) : new Date(0);
        const periodEndDate = newPaycheckDate;
        const itemsToProcess = [...bills, ...debtGoals];
        const batch = writeBatch(db);
        const qBudget = query(collection(db, `/artifacts/${appId}/users/${user.uid}/budgetItems`), where("paycheckId", "==", newPaycheckRef.id));
        const existingBudgetItemsSnap = await getDocs(qBudget);
        const existingBudgetItems = existingBudgetItemsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        for (const item of itemsToProcess) {
            const dueDay = item.dueDay || item.paymentDay;
            if (item.frequency === 'one-time' || !dueDay) continue;
            let dueDateInPeriod = new Date(periodEndDate.getFullYear(), periodEndDate.getMonth(), dueDay);
            if (dueDateInPeriod > periodEndDate) {
                dueDateInPeriod = new Date(periodStartDate.getFullYear(), periodStartDate.getMonth(), dueDay);
            }
            if (dueDateInPeriod >= periodStartDate && dueDateInPeriod <= periodEndDate) {
                const amount = item.amount || item.minPayment;
                const existingItem = existingBudgetItems.find(i => i.categoryId === item.categoryId);
                if (existingItem) {
                    batch.update(doc(db, `/artifacts/${appId}/users/${user.uid}/budgetItems`, existingItem.id), { allocated: (existingItem.allocated || 0) + amount });
                } else {
                    batch.set(doc(collection(db, `/artifacts/${appId}/users/${user.uid}/budgetItems`)), { paycheckId: newPaycheckRef.id, categoryId: item.categoryId, allocated: amount, userId: user.uid });
                }
                batch.set(doc(collection(db, `/artifacts/${appId}/users/${user.uid}/transactions`)), { description: `Auto-paid: ${item.name}`, amount, date: dueDateInPeriod, categoryId: item.categoryId, paycheckId: newPaycheckRef.id, isAutoTransaction: true, userId: user.uid });
            }
        }
        await batch.commit();
        setModal(null);
    };*/
    const showConfirm = (message) => new Promise((resolve) => setModal({ type: 'confirm', message, callback: (result) => { setModal(null); resolve(result); } }));
    /* const handleUpgrade = async () => {
        if (!db || !user) return;
        const settingsRef = doc(db, `/artifacts/${appId}/users/${user.uid}/settings/main`);
        try {
            await updateDoc(settingsRef, { premium: true });
            setModal(null);
        } catch (error) {
            console.error("Error upgrading account:", error);
        }
    }; */

    const handleReorder = async (collectionName, reorderedItems) => {
        if (!db || !user) return;
        const batch = writeBatch(db);
        reorderedItems.forEach((item, index) => {
            const docRef = doc(db, `/artifacts/${appId}/users/${user.uid}/${collectionName}`, item.id);
            batch.update(docRef, { order: index });
        });
        try {
            await batch.commit();
        } catch (error) {
            console.error(`Error reordering ${collectionName}:`, error);
        }
    };

    // --- Render Logic ---
    if (isLoading) { return <div className="bg-off-black text-primary-text min-h-screen flex items-center justify-center"><div className="text-xl font-semibold">Loading Your Stacks...</div></div>; }

    return (
        <div className="bg-off-black text-primary-text h-screen font-sans flex overflow-hidden">
            {isSidebarOpen && (<div onClick={() => setIsSidebarOpen(false)} className="fixed inset-0 bg-black/60 z-20 lg:hidden"></div>)}

            <Sidebar
                user={user}
                onSignUp={() => setAuthModalOpen(true)}
                onSignOut={handleSignOut}
                isSidebarOpen={isSidebarOpen}
                setIsSidebarOpen={setIsSidebarOpen}
            />

            <main className="flex-1 flex flex-col overflow-y-auto scrollbar scrollbar-thumb-stackd-green scrollbar-track-dark-gray">
                <MobileHeader onMenuClick={() => setIsSidebarOpen(true)} />
                <div className="p-4 sm:p-6 lg:p-8 flex-1">
                    <div className="max-w-7xl mx-auto">
                        <Routes>
                            <Route path="/" element={<Navigate to="/app/dashboard" replace />} />
                            <Route path="/dashboard" element={<DashboardView budgetData={budgetData} paychecks={sortedPaychecks} selectedPaycheckId={selectedPaycheckId} setSelectedPaycheckId={setSelectedPaycheckId} setModal={setModal} handleDeletePaycheck={handleDeletePaycheck} />} />
                            <Route path="/budget" element={<BudgetView budgetData={budgetData} paychecks={sortedPaychecks} selectedPaycheckId={selectedPaycheckId} setSelectedPaycheckId={setSelectedPaycheckId} setModal={setModal} handleDelete={handleDelete} handleDeletePaycheck={handleDeletePaycheck} isPremium={isPremium} categories={categories} setCategories={setCategories} handleReorder={handleReorder} />} />
                            <Route path="/transactions" element={<TransactionsView transactions={transactions} paychecks={sortedPaychecks} categories={categories} setModal={setModal} handleDelete={handleDelete} isPremium={isPremium} />} />
                            <Route path="/bills" element={<BillsView bills={bills} setBills={setBills} categories={categories} setModal={setModal} handleDelete={handleDelete} handleReorder={handleReorder} />} />
                            <Route path="/savings" element={<SavingsView savingsGoals={savingsGoals} setSavingsGoals={setSavingsGoals} savingsTransactions={savingsTransactions} setModal={setModal} handleDelete={handleDelete} handleReorder={handleReorder} />} />
                            <Route path="/debt" element={<DebtView debtGoals={debtGoals} setDebtGoals={setDebtGoals} debtPayments={debtPayments} categories={categories} setModal={setModal} handleDelete={handleDelete} geminiModel={geminiModel} isPremium={isPremium} handleReorder={handleReorder} />} />
                            <Route path="/calendar" element={<CalendarView monthlyBills={bills} debtGoals={debtGoals} paychecks={paychecks} />} />
                            <Route path="/learn" element={<LearnView db={db} user={user} geminiModel={geminiModel} debtGoals={debtGoals} savingsGoals={savingsGoals} isPremium={isPremium} setModal={setModal} />} />
                            <Route path="/reporting" element={isPremium ? <ReportingView allTransactions={transactions} categories={categories} paychecks={paychecks} /> : <PremiumGate onUpgradeClick={() => setModal({ type: 'upgrade' })} />} />
                            <Route path="/settings" element={<SettingsView settings={settings} db={db} user={user} isPremium={isPremium} setModal={setModal} onShowIntro={() => { setOnboarding({ inProgress: true, step: 0 }); setShowOnboarding(true); }} />} />
                        </Routes>
                    </div>
                </div>
            </main>

            {/* --- Resume Tour Button --- */}
            {onboarding.inProgress && !showOnboarding && (
                <ResumeTourButton onClick={() => {
                    setOnboarding(prev => ({ ...prev, step: prev.step + 1 }));
                    setShowOnboarding(true);
                }} />
            )}

            {/* --- Modals --- */}
            {modal?.type === 'upgrade' && <UpgradeModal onClose={() => setModal(null)} user={user} setAuthModalOpen={setAuthModalOpen} />}
            {modal?.type === 'addPaycheck' && <AddPaycheckModal onClose={() => setModal(null)} onAdd={(data) => handleAddPaycheckWithContributions(data, db, user, categories, savingsGoals, setModal, budgetItems)} settings={settings} paychecks={sortedPaychecks} savingsGoals={savingsGoals} categories={categories} />}
            {modal?.type === 'addCategory' && <AddCategoryModal onClose={() => setModal(null)} onAdd={handleAdd} />}
            {modal?.type === 'allocate' && <AllocationModal category={modal.data} unallocated={budgetData.unallocated} onClose={() => setModal(null)} onAllocate={(data) => { const budgetItem = budgetData.categoryDetails.find(cd => cd.id === data.categoryId); if (budgetItem?.budgetItemId) { handleUpdate('budgetItems', budgetItem.budgetItemId, { allocated: data.amount }); } else { handleAdd('budgetItems', { paycheckId: selectedPaycheckId, categoryId: data.categoryId, allocated: data.amount }); } setModal(null) }} />}
            {modal?.type === 'addTransaction' && <AddTransactionModal paychecks={sortedPaychecks} categories={categories} onClose={() => setModal(null)} onAdd={handleAdd} />}
            {modal?.type === 'manageRecurringItem' && <ManageRecurringItemModal item={modal.data} type={modal.itemType} categories={categories} onClose={() => setModal(null)} onSave={(collection, id, data) => id ? handleUpdate(collection, id, data) : handleAdd(collection, data)} onAddCategory={(name) => handleAdd('categories', { name }, false)} />}
            {modal?.type === 'scanReceipt' && <AnalyzeReceiptModal categories={categories} paychecks={sortedPaychecks} onAdd={handleAdd} onClose={() => setModal(null)} geminiModel={geminiModel} />}
            {modal?.type === 'addSavingsGoal' && <AddSavingsGoalModal onClose={() => setModal(null)} onAdd={handleAdd} />}
            {modal?.type === 'addSavingsContribution' && <AddContributionModal goal={modal.data} onClose={() => setModal(null)} onAdd={handleAdd} />}
            {modal?.type === 'addDebtPayment' && <AddDebtPaymentModal goal={modal.data} onClose={() => setModal(null)} onAdd={handleAdd} />}
            {modal?.type === 'categoryDetail' && <CategoryDetailModal category={modal.data} onClose={() => setModal(null)} onEditTransaction={(transaction) => setModal({ type: 'editTransaction', data: transaction })} handleDelete={handleDelete} />}
            {modal?.type === 'editTransaction' && <EditTransactionModal transaction={modal.data} paychecks={sortedPaychecks} categories={categories} handleUpdate={handleUpdate} onClose={() => setModal(null)} />}
            {modal?.type === 'confirm' && <ConfirmModal message={modal.message} onResult={modal.callback} />}
            {modal?.type === 'goalDetail' && <GoalDetailModal goalData={modal.data} onClose={() => setModal(null)} onAddContribution={(goal) => setModal({ type: 'addSavingsContribution', data: goal })} onMakeWithdrawal={(goal) => setModal({ type: 'makeWithdrawal', data: goal })} onEditTransaction={(tx) => setModal({ type: 'editSavingsTransaction', data: tx })} onDeleteTransaction={(id) => handleDelete('savingsTransactions', id)} />}
            {modal?.type === 'makeWithdrawal' && <AddContributionModal goal={modal.data} onClose={() => setModal(null)} onAdd={handleAdd} isWithdrawal={true} />}
            {modal?.type === 'editSavingsTransaction' && <EditSavingsTransactionModal transaction={modal.data} onClose={() => setModal(null)} onUpdate={handleUpdate} />}

            {authModalOpen && <AuthModal onClose={() => setAuthModalOpen(false)} onGoogleSignIn={handleGoogleSignIn} error={authError} />}

            {showOnboarding && (
                <OnboardingModal
                    onboarding={onboarding}
                    setOnboarding={setOnboarding}
                    setShowOnboarding={setShowOnboarding}
                />
            )}
        </div>
    );
}



// --- View Components ---
const DashboardView = ({ budgetData, paychecks, selectedPaycheckId, setSelectedPaycheckId, setModal, handleDeletePaycheck }) => {
    const spendingData = budgetData.categoryDetails.filter(c => c.spent > 0).map(c => ({ name: c.name, spent: c.spent }));
    const tooltipStyle = { background: '#111111', border: '1px solid rgba(229, 231, 235, 0.1)', borderRadius: '0.5rem', color: '#E5E7EB' };

    return (
        <div>
            <Header title="Dashboard" subtitle="Here is the overview of your financial world." />
            <PaycheckSelector paychecks={paychecks} selectedPaycheckId={selectedPaycheckId} setSelectedPaycheckId={setSelectedPaycheckId} onAddPaycheck={() => setModal({ type: 'addPaycheck' })} onDeletePaycheck={handleDeletePaycheck} />
            {paychecks.length === 0 ? (
                <Welcome onAddPaycheck={() => setModal({ type: 'addPaycheck' })} />
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
                    <Card className="lg:col-span-2 p-6">
                        <h3 className="text-lg font-semibold text-primary-text mb-4">Spending Breakdown</h3>
                        {spendingData.length > 0 ? (
                            <div style={{ width: '100%', height: 300 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={spendingData} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                                        <XAxis dataKey="name" stroke={'#A0A0A0'} fontSize={12} tickLine={false} axisLine={false} />
                                        <YAxis stroke={'#A0A0A0'} fontSize={12} tickLine={false} axisLine={false} tickFormatter={(value) => `$${value}`} />
                                        <Tooltip cursor={{ fill: 'rgba(160, 160, 160, 0.1)' }} contentStyle={tooltipStyle} />
                                        <Bar dataKey="spent" fill="#05F56A" radius={[4, 4, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        ) : (
                            <p className="text-secondary-text text-center py-20">Log some spending to see your stack breakdown.</p>
                        )}
                    </Card>
                    <div className="space-y-6">
                        {/* The new AllocationMeter is added here */}
                        <AllocationMeter allocated={budgetData.totalAllocated} total={budgetData.paycheckTotal} />
                        <SummaryCard title="Unallocated" value={formatCurrency(budgetData.unallocated)} />
                        <SummaryCard title="Total Spent" value={formatCurrency(budgetData.totalSpent)} />
                    </div>
                </div>
            )}
        </div>
    );
};

const BudgetView = ({ budgetData, paychecks, selectedPaycheckId, setSelectedPaycheckId, setModal, handleDelete, handleDeletePaycheck, isPremium, categories, setCategories, handleReorder }) => {
    const onDragEnd = (result) => {
        const { destination, source } = result;
        if (!destination || (destination.droppableId === source.droppableId && destination.index === source.index)) {
            return;
        }
        const items = Array.from(categories);
        const [reorderedItem] = items.splice(source.index, 1);
        items.splice(destination.index, 0, reorderedItem);
        setCategories(items); // Optimistic update for smooth UI
        handleReorder('categories', items);
    };

    return (
        <div>
            <Header title="Your Budget" subtitle="Assign every dollar a job. This is your blueprint." />
            <PaycheckSelector paychecks={paychecks} selectedPaycheckId={selectedPaycheckId} setSelectedPaycheckId={setSelectedPaycheckId} onAddPaycheck={() => setModal({ type: 'addPaycheck' })} onDeletePaycheck={handleDeletePaycheck} />
            <div className="mt-6 flex justify-end gap-4 flex-wrap">
                {isPremium ? (
                    <AiButton onClick={() => setModal({ type: 'scanReceipt' })}>
                        Scan Receipt
                    </AiButton>
                ) : (
                    <div className="relative">
                        <SecondaryButton disabled={true}>Scan Receipt (Premium)</SecondaryButton>
                        <PremiumLock onClick={() => setModal({ type: 'upgrade' })} />
                    </div>
                )}
                <PrimaryButton onClick={() => setModal({ type: 'addCategory' })}>
                    <PlusCircleIcon /> New Category
                </PrimaryButton>
            </div>
            <Card className="mt-6 p-6">
                <h2 className="text-xl font-semibold mb-4 text-primary-text border-b border-border-color pb-2">Category Breakdown</h2>
                <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="categories">
                        {(provided) => (
                            <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-4">
                                {budgetData.categoryDetails.map((cat, index) => (
                                    <Draggable key={cat.id} draggableId={cat.id} index={index}>
                                        {(provided) => (
                                            <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                                                <CategoryItem category={cat} onAddTransaction={() => setModal({ type: 'addTransaction', data: cat })} onAllocate={() => setModal({ type: 'allocate', data: cat })} onDelete={() => handleDelete('categories', cat.id)} onShowDetail={() => setModal({ type: 'categoryDetail', data: cat })} />
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            </Card>
        </div>
    );
};

const BillsView = ({ bills, setBills, categories, setModal, handleDelete, handleReorder }) => {
    const getCategoryName = (id) => categories.find(c => c.id === id)?.name || 'N/A';

    const onDragEnd = (result) => {
        const { destination, source } = result;
        if (!destination || (destination.droppableId === source.droppableId && destination.index === source.index)) {
            return;
        }
        const items = Array.from(bills);
        const [reorderedItem] = items.splice(source.index, 1);
        items.splice(destination.index, 0, reorderedItem);
        setBills(items); // Optimistic UI update
        handleReorder('monthlyBills', items);
    };

    return (
        <div>
            <Header title="Bills" subtitle="Manage your recurring payments." />
            <div className="flex justify-end mb-6">
                <PrimaryButton onClick={() => setModal({ type: 'manageRecurringItem', itemType: 'bill', data: null })}>
                    <PlusCircleIcon /> Add Bill
                </PrimaryButton>
            </div>
            <Card className="p-6">
                {bills.length > 0 ? (
                    <DragDropContext onDragEnd={onDragEnd}>
                        <Droppable droppableId="bills">
                            {(provided) => (
                                <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-3">
                                    {bills.map((bill, index) => (
                                        <Draggable key={bill.id} draggableId={bill.id} index={index}>
                                            {(provided) => (
                                                <div ref={provided.innerRef} {...provided.draggableProps}>
                                                    <Card className="flex items-center justify-between p-3 bg-off-black">
                                                        <div className="flex items-center gap-2">
                                                            <span {...provided.dragHandleProps} className="text-secondary-text cursor-grab"><GripVerticalIcon /></span>
                                                            <div>
                                                                <p className="font-semibold text-primary-text">{bill.name}</p>
                                                                <p className="text-sm text-secondary-text">{getCategoryName(bill.categoryId)} - <span className="capitalize">{bill.frequency}</span></p>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-4">
                                                            <span className="font-semibold text-lg text-primary-text">{formatCurrency(bill.amount)}</span>
                                                            <button onClick={() => setModal({ type: 'manageRecurringItem', itemType: 'bill', data: bill })} className="text-secondary-text hover:text-stackd-green"><EditIcon /></button>
                                                            <button onClick={() => handleDelete('monthlyBills', bill.id)} className="text-secondary-text hover:text-error"><TrashIcon /></button>
                                                        </div>
                                                    </Card>
                                                </div>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                ) : (
                    <p className="text-secondary-text text-center py-8">No bills added. Time to get organized!</p>
                )}
            </Card>
        </div>
    );
};

const SavingsView = ({ savingsGoals, setSavingsGoals, savingsTransactions, setModal, handleDelete, handleReorder }) => {
    const onDragEnd = (result) => {
        const { destination, source } = result;
        if (!destination || (destination.droppableId === source.droppableId && destination.index === source.index)) {
            return;
        }
        const items = Array.from(savingsGoals);
        const [reorderedItem] = items.splice(source.index, 1);
        items.splice(destination.index, 0, reorderedItem);
        setSavingsGoals(items);
        handleReorder('savingsGoals', items);
    };

    return (
        <div>
            <Header title="Your Goals" subtitle="Build towards your future stacks." />
            <div className="flex justify-end mb-6">
                <PrimaryButton onClick={() => setModal({ type: 'addSavingsGoal' })}>
                    <PlusCircleIcon /> New Goal
                </PrimaryButton>
            </div>

            {/* --- Conditional Rendering Logic --- */}
            {savingsGoals.length === 0 ? (
                // If there are no goals, show the EmptyState component
                <EmptyState
                    icon={<SavingsIcon />}
                    title="Set Your First Goal"
                    message="What are you stacking for? Creating a goal gives your money purpose and helps you build the future you want."
                    buttonText="Add First Goal"
                    onButtonClick={() => setModal({ type: 'addSavingsGoal' })}
                />
            ) : (
                // Otherwise, show the list of goals
                <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="savingsGoals">
                        {(provided) => (
                            <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-6">
                                {savingsGoals.map((goal, index) => {
                                    const transactionsForGoal = savingsTransactions.filter(t => t.goalId === goal.id);
                                    return (
                                        <Draggable key={goal.id} draggableId={goal.id} index={index}>
                                            {(provided) => (
                                                <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                                                    <SavingsGoalItem
                                                        goal={goal}
                                                        transactions={transactionsForGoal}
                                                        onDelete={() => handleDelete('savingsGoals', goal.id)}
                                                        onShowDetail={() => setModal({ type: 'goalDetail', data: { goal, transactions: transactionsForGoal } })}
                                                    />
                                                </div>
                                            )}
                                        </Draggable>
                                    )
                                })}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            )}
        </div>
    );
};

const DebtView = ({ debtGoals, setDebtGoals, debtPayments, categories, setModal, handleDelete, geminiModel, isPremium, handleReorder }) => {
    const onDragEnd = (result) => {
        const { destination, source } = result;
        if (!destination || (destination.droppableId === source.droppableId && destination.index === source.index)) {
            return;
        }
        const items = Array.from(debtGoals);
        const [reorderedItem] = items.splice(source.index, 1);
        items.splice(destination.index, 0, reorderedItem);
        setDebtGoals(items);
        handleReorder('debtGoals', items);
    };

    return (
        <div>
            <Header title="Debt Paydown" subtitle="Tackle your debt and build financial freedom." />
            <div className="flex justify-end mb-6">
                <PrimaryButton onClick={() => setModal({ type: 'manageRecurringItem', itemType: 'debt', data: null })}>
                    <PlusCircleIcon /> Add Debt
                </PrimaryButton>
            </div>
            <div className="relative">
                <AIStrategyAdvisor debtGoals={debtGoals} geminiModel={geminiModel} />
                {!isPremium && <PremiumLock onClick={() => setModal({ type: 'upgrade' })} />}
            </div>
            <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="debtGoals">
                    {(provided) => (
                        <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-6 mt-6">
                            {debtGoals.map((goal, index) => (
                                <Draggable key={goal.id} draggableId={goal.id} index={index}>
                                    {(provided) => (
                                        <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                                            <DebtGoalItem goal={goal} payments={debtPayments.filter(p => p.goalId === goal.id)} onAddPayment={() => setModal({ type: 'addDebtPayment', data: goal })} onEdit={() => setModal({ type: 'manageRecurringItem', itemType: 'debt', data: goal })} onDelete={() => handleDelete('debtGoals', goal.id)} />
                                        </div>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
        </div>
    );
};

const TransactionsView = ({ transactions, paychecks, categories, setModal, handleDelete, isPremium }) => {
    const [filterPaycheckId, setFilterPaycheckId] = useState('all');
    const getCategoryName = (id) => categories.find(c => c.id === id)?.name || 'N/A';
    const getPaycheckName = (id) => paychecks.find(p => p.id === id)?.name || 'Unassigned';

    const filteredTransactions = useMemo(() => {
        if (filterPaycheckId === 'all') return transactions;
        return transactions.filter(t => t.paycheckId === filterPaycheckId);
    }, [filterPaycheckId, transactions]);

    return (
        <div>
            <Header title="All Transactions" subtitle="View and manage all your spending." />
            <div className="flex justify-end gap-4 mb-6">
                {/* The Scan Receipt button is added here */}
                {isPremium ? (
                    <AiButton onClick={() => setModal({ type: 'scanReceipt' })}>
                        Scan Receipt
                    </AiButton>
                ) : (
                    <div className="relative">
                        <SecondaryButton disabled={true}>Scan Receipt (Premium)</SecondaryButton>
                        <PremiumLock onClick={() => setModal({ type: 'upgrade' })} />
                    </div>
                )}
                <PrimaryButton onClick={() => setModal({ type: 'addTransaction' })}>
                    <PlusCircleIcon /> Add Transaction
                </PrimaryButton>
            </div>
            <Card className="mb-6 p-4">
                <label className="font-semibold text-primary-text mr-3">Filter by Paycheck:</label>
                <select value={filterPaycheckId} onChange={(e) => setFilterPaycheckId(e.target.value)} className="form-input inline-block w-auto">
                    <option value="all">All Paychecks</option>
                    {paychecks.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
            </Card>

            <Card className="p-6">
                <div className="space-y-3">
                    {filteredTransactions.length > 0 ? filteredTransactions.map(t => (
                        <Card key={t.id} className="flex items-center justify-between p-3 bg-off-black hover:border-border-color/50">
                            <div className="flex-1">
                                <p className="text-primary-text font-semibold">{t.description}</p>
                                <p className="text-xs text-secondary-text">
                                    {getCategoryName(t.categoryId)} | {getPaycheckName(t.paycheckId)} | {t.date?.seconds ? new Date(t.date.seconds * 1000).toLocaleDateString() : ''}
                                </p>
                            </div>
                            <div className="flex items-center gap-4">
                                <p className="font-semibold text-primary-text text-lg">{formatCurrency(t.amount)}</p>
                                <button onClick={() => setModal({ type: 'editTransaction', data: t })} className="text-secondary-text hover:text-warning"><EditIcon /></button>
                                <button onClick={() => handleDelete('transactions', t.id)} className="text-secondary-text hover:text-error"><TrashIcon /></button>
                            </div>
                        </Card>
                    )) : <p className="text-secondary-text text-center py-8">No transactions found for this filter.</p>}
                </div>
            </Card>
        </div>
    );
};

const ReportingView = ({ allTransactions = [], categories = [], paychecks = [] }) => {
    const today = new Date();
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];

    const [startDate, setStartDate] = useState(startOfMonth);
    const [endDate, setEndDate] = useState(today.toISOString().split('T')[0]);
    const [paycheckFilter, setPaycheckFilter] = useState('all');

    const reportingData = useMemo(() => {
        const filteredTransactions = allTransactions.filter(t => {
            if (!t.date?.seconds) return false;
            const txDate = new Date(t.date.seconds * 1000);

            const start = startDate ? parseDateStringAsLocal(startDate) : null;
            const end = endDate ? parseDateStringAsLocal(endDate) : null;
            // Set end date to the end of the day for accurate filtering
            if (end) end.setHours(23, 59, 59, 999);

            const isAfterStart = !start || txDate >= start;
            const isBeforeEnd = !end || txDate <= end;
            const matchesPaycheck = paycheckFilter === 'all' || t.paycheckId === paycheckFilter;

            return isAfterStart && isBeforeEnd && matchesPaycheck;
        });

        const categorySpending = categories.map(cat => {
            const totalSpent = filteredTransactions
                .filter(t => t.categoryId === cat.id)
                .reduce((sum, t) => sum + (t.amount || 0), 0);
            return { name: cat.name, spent: totalSpent };
        }).filter(c => c.spent > 0);

        return categorySpending.sort((a, b) => b.spent - a.spent);
    }, [allTransactions, categories, startDate, endDate, paycheckFilter]);

    const totalSpentInPeriod = reportingData.reduce((sum, item) => sum + item.spent, 0);
    const tooltipStyle = { background: '#111111', border: '1px solid rgba(229, 231, 235, 0.1)', borderRadius: '0.5rem', color: '#E5E7EB' };

    return (
        <div>
            <Header title="Advanced Reporting" subtitle="Get a high-level overview of your spending habits." />

            <Card className="p-4 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                    <div>
                        <label className="form-label">Start Date</label>
                        <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="form-input" />
                    </div>
                    <div>
                        <label className="form-label">End Date</label>
                        <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="form-input" />
                    </div>
                    <div>
                        <label className="form-label">Filter by Paycheck</label>
                        <select value={paycheckFilter} onChange={e => setPaycheckFilter(e.target.value)} className="form-input">
                            <option value="all">All Paychecks</option>
                            {paychecks.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                    </div>
                </div>
            </Card>

            <Card className="p-6">
                <div className="flex justify-between items-baseline mb-4">
                    <h3 className="text-lg font-semibold text-primary-text">Spending by Category</h3>
                    <p className="text-secondary-text">Total Spent: <span className="font-bold text-primary-text">{formatCurrency(totalSpentInPeriod)}</span></p>
                </div>
                {reportingData.length > 0 ? (
                    <div style={{ width: '100%', height: 400 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={reportingData} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                                <XAxis type="number" stroke="#A0A0A0" tickFormatter={formatCurrency} />
                                <YAxis type="category" dataKey="name" stroke="#A0A0A0" width={120} interval={0} />
                                <Tooltip cursor={{ fill: 'rgba(160, 160, 160, 0.1)' }} contentStyle={tooltipStyle} formatter={formatCurrency} />
                                <Bar dataKey="spent" radius={[0, 4, 4, 0]}>
                                    {reportingData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill="#05F56A" />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                ) : (
                    <p className="text-secondary-text text-center py-20">No spending data available for the selected period.</p>
                )}
            </Card>
        </div>
    );
};

const CalendarView = ({ monthlyBills = [], debtGoals = [], paychecks = [] }) => {
    const [currentDate, setCurrentDate] = useState(new Date());
    const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    const daysInMonth = endOfMonth.getDate();
    const startDay = startOfMonth.getDay();
    const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
    const blanks = Array.from({ length: startDay }, (_, i) => null);
    const allCells = [...blanks, ...days];
    const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const changeMonth = (offset) => { setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + offset, 1)); };

    return (
        <div>
            <Header title="Calendar" subtitle="Visualize your drops and due dates." />
            <Card className="p-6">
                <div className="flex justify-between items-center mb-4">
                    <button onClick={() => changeMonth(-1)} className="p-2 rounded-md hover:bg-off-black">&larr;</button>
                    <h3 className="text-lg font-semibold">{currentDate.toLocaleString('default', { month: 'long', year: 'numeric' })}</h3>
                    <button onClick={() => changeMonth(1)} className="p-2 rounded-md hover:bg-off-black">&rarr;</button>
                </div>
                <div className="grid grid-cols-7 gap-1 sm:gap-2">
                    {weekdays.map(day => <div key={day} className="text-center font-bold text-secondary-text text-sm">{day}</div>)}
                    {allCells.map((day, index) => {
                        const billsOnDay = day ? monthlyBills.filter(b => b.dueDay === day) : [];
                        const debtsOnDay = day ? debtGoals.filter(d => d.paymentDay === day) : [];
                        const paychecksOnDay = day ? paychecks.filter(p => p.date?.seconds && new Date(p.date.seconds * 1000).getDate() === day && new Date(p.date.seconds * 1000).getMonth() === currentDate.getMonth()) : [];
                        return (
                            <div key={index} className={`h-28 p-1 border border-border-color rounded-lg ${day ? 'bg-off-black' : 'bg-dark-gray'}`}>
                                {day && <span className="text-sm">{day}</span>}
                                <div className="text-xs mt-1 space-y-1 overflow-auto h-20">
                                    {billsOnDay.map(b => <div key={b.id} className="bg-error/20 text-error p-1 rounded truncate" title={b.name}>{b.name}</div>)}
                                    {debtsOnDay.map(d => <div key={d.id} className="bg-warning/20 text-warning p-1 rounded truncate" title={d.name}>{d.name}</div>)}
                                    {paychecksOnDay.map(p => <div key={p.id} className="bg-stackd-green/20 text-stackd-green p-1 rounded truncate" title={p.name}>{p.name}</div>)}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </Card>
        </div>
    );
};

const LearnWelcome = () => (
    <div className="text-center flex flex-col items-center justify-center h-full">
        <div className="p-4 bg-stackd-green/10 rounded-full mb-4">
            <LearnIcon className="w-8 h-8 text-stackd-green" />
        </div>
        <h2 className="text-2xl font-bold text-primary-text">Your Financial Command Center</h2>
        <p className="mt-2 text-secondary-text max-w-md">
            Select a <strong>Personalized Analysis</strong> for AI-driven insights on your own data, or explore the <strong>Learning Path</strong> to build your financial knowledge from the ground up.
        </p>
    </div>
);

const LearnView = ({ db, user, geminiModel, debtGoals, savingsGoals, isPremium, setModal }) => {
    // --- STATE AND DATA ---
    const [article, setArticle] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [activeTopic, setActiveTopic] = useState('');
    const [completedTopics, setCompletedTopics] = useState(new Set());
    const [isPersonalized, setIsPersonalized] = useState(false);
    const [activeTab, setActiveTab] = useState('analysis');
    const learningPath = {
        "Level 1: Foundations": ["Mind Over Money: Understanding Your Financial Habits", "The Art of Budgeting: Giving Every Dollar a Job", "Building Your Financial Shield: The Emergency Fund", "Decoding Debt: Good Debt vs. Bad Debt", "Your Credit Score Explained: More Than Just a Number",],
        "Level 2: Growth": ["Investing 101: Making Your Money Work for You", "Retirement 101: 401(k) vs. Roth IRA", "Passive Investing: The Power of Index Funds & ETFs", "Beyond the Bank: High-Yield Savings Accounts (HYSAs)", "Saving for Big Goals: Home Down Payments & More",],
        "Level 3: Advanced": ["Your Biggest Asset: Strategies to Maximize Your Income", "Individual Stocks: Risk, Reward, and Doing Your Research", "Understanding Net Worth: Your True Financial Snapshot", "The FIRE Movement: Financial Independence, Retire Early",]
    };

    // --- EFFECTS ---
    useEffect(() => {
        if (db && user) {
            const progressRef = collection(db, `/artifacts/${appId}/users/${user.uid}/learning_progress`);
            const unsubscribe = onSnapshot(progressRef, (snapshot) => {
                const completed = new Set();
                snapshot.forEach(doc => completed.add(doc.data().topic));
                setCompletedTopics(completed);
            });
            return () => unsubscribe();
        }
    }, [db, user]);

    // --- HANDLERS ---
    const fetchArticle = async (selectedTopic) => {
        if (!db) { console.error("Firestore is not initialized yet."); setArticle("Database connection is not ready."); return; }
        setIsLoading(true); setArticle(''); setActiveTopic(selectedTopic); setIsPersonalized(false);
        try {
            const articlesRef = collection(db, 'learning_articles');
            const q = query(articlesRef, where("topic", "==", selectedTopic));
            const querySnapshot = await getDocs(q);
            const existingArticles = [];
            querySnapshot.forEach(doc => { existingArticles.push(doc.data()); });

            if (existingArticles.length > 0) {
                const randomIndex = Math.floor(Math.random() * existingArticles.length);
                setArticle(existingArticles[randomIndex].content);
            } else if (isPremium) {
                if (!geminiModel) { setArticle("AI model is not ready."); setIsLoading(false); return; }
                const prompt = `You are a financial literacy coach with a modern, edgy tone for a younger audience using an app called STACKD. Write a short, easy-to-understand article about "${selectedTopic}". Use simple language, short paragraphs, and provide actionable tips. Format using simple markdown.`;
                const result = await geminiModel.generateContent(prompt);
                const newArticleContent = result.response.text();
                setArticle(newArticleContent);
                await addDoc(collection(db, 'learning_articles'), { topic: selectedTopic, content: newArticleContent, createdAt: serverTimestamp() });
            } else {
                setArticle("This premium article is waiting for you. Upgrade to generate it instantly and unlock our AI co-pilot.");
            }
        } catch (error) {
            console.error("Error fetching or generating article:", error);
            setArticle("Sorry, we couldn't fetch this article right now.");
        } finally {
            setIsLoading(false);
        }
    };

    const handlePersonalizedAnalysis = async (analysisType) => {
        if (!isPremium) {
            setModal({ type: 'upgrade' });
            return;
        }
        if (!geminiModel || !user) {
            setArticle("The AI model or user data is not available. Please try again.");
            return;
        }

        setIsLoading(true);
        setArticle('');
        setActiveTopic(analysisType);
        setIsPersonalized(true);

        let prompt;
        let dataSummary;

        if (analysisType === 'Analyze My Debt') {
            if (debtGoals.length === 0) {
                setArticle("You haven't added any debts yet. Add a debt in the 'Debt' section to get a personalized analysis.");
                setIsLoading(false);
                return;
            }
            dataSummary = debtGoals.map(d => `- ${d.name}: Balance of ${formatCurrency(d.totalAmount)} at ${d.interestRate}% APR`).join('\n');
            prompt = `As a financial coach for the app STACKD, analyze my user's specific debt situation and provide actionable advice. Their debts are:\n${dataSummary}\n\n1. Recommend either the "Debt Snowball" or "Debt Avalanche" method and explain *why* it's the better choice for their specific numbers.\n2. Provide a clear, step-by-step list of which debt to focus on first.\n3. Offer encouragement and keep the tone modern and positive. Format the output using simple markdown.`;
        } else if (analysisType === 'Review My Savings') {
            if (savingsGoals.length === 0) {
                setArticle("You haven't added any savings goals yet. Add a goal in the 'Goals' section for a personalized review.");
                setIsLoading(false);
                return;
            }
            dataSummary = savingsGoals.map(g => `- ${g.name}: Goal of ${formatCurrency(g.goalAmount)}`).join('\n');
            prompt = `As a financial coach for the app STACKD, analyze my user's savings goals and provide actionable advice. Their goals are:\n${dataSummary}\n\n1. Briefly praise their effort in setting these goals.\n2. Offer one or two practical tips on how to reach these goals faster (e.g., automating contributions, finding small ways to save more).\n3. Keep the tone encouraging and motivational. Format the output using simple markdown.`;
        }

        try {
            const result = await geminiModel.generateContent(prompt);
            const analysisContent = result.response.text();
            setArticle(analysisContent);

            await addDoc(collection(db, `/artifacts/${appId}/users/${user.uid}/personalized_analyses`), {
                analysisType: analysisType,
                content: analysisContent,
                dataSummary: dataSummary,
                createdAt: serverTimestamp()
            });

        } catch (error) {
            console.error("Error generating personalized analysis:", error);
            setArticle("Sorry, the AI advisor couldn't complete the analysis right now. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const markTopicAsComplete = async (topic) => {
        if (!db || !user) return;
        const topicId = topic.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '');
        const progressRef = doc(db, `/artifacts/${appId}/users/${user.uid}/learning_progress`, topicId);
        try {
            await setDoc(progressRef, {
                topic: topic,
                completedAt: serverTimestamp()
            });
        } catch (error) {
            console.error("Error saving progress:", error);
        }
    };

    // --- RENDER ---
    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-1">
                <Header title="Learn" subtitle="Your financial command center." />
                <div className="flex border-b border-border-color mb-4">
                    <button onClick={() => setActiveTab('analysis')} className={`py-2 px-4 font-semibold text-sm transition-colors ${activeTab === 'analysis' ? 'border-b-2 border-stackd-green text-primary-text' : 'text-secondary-text hover:text-primary-text'}`}>
                        Personalized Analysis
                    </button>
                    <button onClick={() => setActiveTab('path')} className={`py-2 px-4 font-semibold text-sm transition-colors ${activeTab === 'path' ? 'border-b-2 border-stackd-green text-primary-text' : 'text-secondary-text hover:text-primary-text'}`}>
                        Learning Path
                    </button>
                </div>
                <div className="space-y-4">
                    {activeTab === 'analysis' && (
                        <div className="space-y-2 animate-fade-in">
                            <p className="text-sm text-secondary-text pb-2">Get AI-driven insights based on your actual financial data.</p>
                            <div className="relative">
                                <AiButton onClick={() => handlePersonalizedAnalysis('Analyze My Debt')} className="w-full justify-start" disabled={isLoading}>Analyze My Debt</AiButton>
                                {!isPremium && <PremiumLock onClick={() => setModal({ type: 'upgrade' })} />}
                            </div>
                            <div className="relative">
                                <AiButton onClick={() => handlePersonalizedAnalysis('Review My Savings')} className="w-full justify-start" disabled={isLoading}>Review My Savings</AiButton>
                                {!isPremium && <PremiumLock onClick={() => setModal({ type: 'upgrade' })} />}
                            </div>
                        </div>
                    )}
                    {activeTab === 'path' && (
                        <div className="space-y-2 animate-fade-in">
                            <p className="text-sm text-secondary-text pb-2">Build your financial knowledge from the ground up. Start with Level 1.</p>
                            {Object.entries(learningPath).map(([level, topics]) => {
                                const isLocked = (level.includes("Level 2") || level.includes("Level 3")) && !isPremium;
                                return (
                                    <div key={level} className="relative">
                                        <details className="bg-dark-gray rounded-lg group" open={level === "Level 1: Foundations"}>
                                            <summary className={`p-3 font-semibold text-md cursor-pointer list-outside flex justify-between items-center group-hover:bg-off-black/50 rounded-t-lg ${isLocked ? 'text-secondary-text' : 'text-stackd-green'}`}>
                                                {level}
                                                {!isLocked && <span className="text-secondary-text transform transition-transform duration-200 group-open:rotate-90">{'>'}</span>}
                                            </summary>
                                            {!isLocked && (
                                                <div className="space-y-2 p-3 border-t border-border-color">
                                                    {topics.map(topic => (
                                                        <button
                                                            key={topic}
                                                            onClick={() => fetchArticle(topic)}
                                                            disabled={isLoading}
                                                            className={`w-full text-left p-3 rounded-lg transition-colors text-sm flex items-center justify-between disabled:opacity-50 ${activeTopic === topic ? 'bg-off-black text-primary-text' : 'bg-transparent text-secondary-text hover:text-primary-text hover:bg-off-black/30'}`}
                                                        >
                                                            <span>{topic}</span>
                                                            {completedTopics.has(topic) && <span className="text-stackd-green">✓</span>}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </details>
                                        {isLocked && <PremiumLock onClick={() => setModal({ type: 'upgrade' })} />}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
            <div className="lg:col-span-2">
                <Card className="p-6 sticky top-8 min-h-[80vh]">
                    <div className="prose prose-invert max-w-none h-full max-h-[calc(80vh-3rem)] overflow-y-auto pr-4 scrollbar-thin scrollbar-thumb-stackd-green/80 scrollbar-track-dark-gray/50 hover:scrollbar-thumb-stackd-green">
                        {isLoading && <p className="text-center">Loading...</p>}
                        {!isLoading && article && (
                            <>
                                <h2 className="text-2xl font-bold text-primary-text mb-4">{activeTopic}</h2>
                                <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(marked.parse(article)) }} />
                                {!isPersonalized && activeTopic && !completedTopics.has(activeTopic) && (
                                    <div className="my-8 flex justify-center">
                                        <PrimaryButton onClick={() => markTopicAsComplete(activeTopic)}>Mark as Complete</PrimaryButton>
                                    </div>
                                )}
                                {isPremium ? (
                                    <AIChat geminiModel={geminiModel} articleContent={article} />
                                ) : (
                                    article && (
                                        <div className="mt-8 border-t border-border-color pt-6 text-center">
                                            <h4 className="text-lg font-semibold text-primary-text">Unlock Your AI Co-Pilot</h4>
                                            <p className="text-secondary-text my-2 max-w-sm mx-auto">Upgrade to Premium to ask follow-up questions and get personalized advice on this topic.</p>
                                            <div className="mt-4 flex justify-center">
                                                <PrimaryButton onClick={() => setModal({ type: 'upgrade' })}>
                                                    <StackIcon /> Unlock Now
                                                </PrimaryButton>
                                            </div>
                                        </div>
                                    )
                                )}
                            </>
                        )}
                        {!isLoading && !article && <LearnWelcome />}
                    </div>
                </Card>
            </div>
        </div>
    );
};

const AIChat = ({ geminiModel, articleContent }) => {
    const [messages, setMessages] = useState([]);
    const [userInput, setUserInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const chatContainerRef = useRef(null);

    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [messages]);

    // Reset chat when a new article is loaded
    useEffect(() => {
        setMessages([]);
    }, [articleContent]);

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!userInput.trim() || isLoading || !geminiModel) return;

        const newUserMessage = { role: 'user', text: userInput };
        setMessages(prev => [...prev, newUserMessage]);
        setUserInput('');
        setIsLoading(true);

        try {
            const systemPrompt = `CONTEXT: The user has just finished reading the following article or analysis about their personal finance. Your role is a helpful financial coach. Answer their follow-up questions based on the article's context and your general knowledge. Keep your answers concise and easy to understand.\n\n---CONTEXT START---\n${articleContent}\n---CONTEXT END---\n`;

            const historyForAPI = [
                { role: 'user', parts: [{ text: systemPrompt }] },
                { role: 'model', parts: [{ text: "Great! I've reviewed the information. What specific questions do you have?" }] },
                ...messages.map(msg => ({ role: msg.role, parts: [{ text: msg.text }] })),
                { role: 'user', parts: [{ text: userInput }] }
            ];

            const result = await geminiModel.generateContent({ contents: historyForAPI });
            const modelResponse = result.response.text();
            setMessages(prev => [...prev, { role: 'model', text: modelResponse }]);

        } catch (error) {
            console.error("AI Chat Error:", error);
            setMessages(prev => [...prev, { role: 'model', text: "Sorry, I encountered an error. Please try again." }]);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="mt-8 border-t border-border-color pt-6">
            <h4 className="text-lg font-semibold mb-2 text-primary-text">Ask a Follow-Up Question</h4>
            <div ref={chatContainerRef} className="h-64 overflow-y-auto bg-off-black p-3 rounded-lg space-y-3 mb-3 border border-border-color">
                {messages.map((msg, index) => (
                    <div key={index} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-xs md:max-w-md lg:max-w-lg p-3 rounded-lg ${msg.role === 'user' ? 'bg-stackd-green text-black' : 'bg-dark-gray'}`}>
                            <div className="text-sm" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(marked.parse(msg.text)) }} />
                        </div>
                    </div>
                ))}
                {isLoading && (
                    <div className="flex justify-start">
                        <div className="bg-dark-gray p-3 rounded-lg">
                            <p className="text-sm italic text-secondary-text">Stackd AI is typing...</p>
                        </div>
                    </div>
                )}
            </div>
            <form onSubmit={handleSendMessage} className="flex gap-2">
                <input
                    type="text"
                    value={userInput}
                    onChange={(e) => setUserInput(e.target.value)}
                    placeholder="Ask anything about this topic..."
                    className="form-input flex-1"
                    disabled={isLoading}
                />
                <PrimaryButton type="submit" disabled={isLoading || !userInput.trim()}>Send</PrimaryButton>
            </form>
        </div>
    );
};

const SettingsView = ({ settings, db, user, isPremium, setModal, onShowIntro }) => {
    const [frequency, setFrequency] = useState(settings.paycheckFrequency || 'manual');
    const [nextDate, setNextDate] = useState(settings.nextPaycheckDate || '');
    const [defaultAmount, setDefaultAmount] = useState(settings.defaultPaycheckAmount || '');

    const handleSave = async () => {
        if (!db || !user) return;
        const settingsRef = doc(db, `/artifacts/${appId}/users/${user.uid}/settings/main`);
        const settingsData = {
            paycheckFrequency: frequency,
            nextPaycheckDate: nextDate,
            defaultPaycheckAmount: parseFloat(defaultAmount) || 0
        };
        await writeBatch(db).set(settingsRef, settingsData, { merge: true }).commit();
    };

    return (
        <div>
            <Header title="Settings" subtitle="Customize your Stackd experience." />
            <Card className="p-6 space-y-6">
                <div>
                    <h3 className="text-lg font-semibold text-primary-text">Subscription Status</h3>
                    <Card className="flex items-center justify-between p-4 bg-off-black mt-2">
                        <div>
                            <p className={`font-bold text-xl ${isPremium ? 'text-stackd-green' : 'text-primary-text'}`}>{isPremium ? 'STACKD PREMIUM' : 'FREE TIER'}</p>
                            <p className="text-sm text-secondary-text">{isPremium ? 'All features unlocked. You crushed it.' : 'Upgrade to unlock AI features.'}</p>
                        </div>
                        {!isPremium && (
                            <PrimaryButton onClick={() => setModal({ type: 'upgrade' })}>Upgrade</PrimaryButton>
                        )}
                    </Card>
                </div>
                <div>
                    <h3 className="text-lg font-semibold text-primary-text">Default Paycheck Amount</h3>
                    <p className="text-secondary-text text-sm mt-1 mb-4">Set a default amount to pre-fill the form when adding a new paycheck.</p>
                    <CurrencyInput
                        placeholder="0.00"
                        value={defaultAmount}
                        onChange={e => setDefaultAmount(e.target.value)}
                        className="max-w-xs"
                    />
                </div>
                <div>
                    <h3 className="text-lg font-semibold text-primary-text">Paycheck Frequency</h3>
                    <p className="text-secondary-text text-sm mt-1 mb-4">Set this to automate bill allocation. 'Manual' is for irregular income.</p>
                    <select value={frequency} onChange={e => setFrequency(e.target.value)} className="form-input max-w-xs">
                        <option value="manual">Manual</option>
                        <option value="weekly">Weekly</option>
                        <option value="bi-weekly">Bi-Weekly</option>
                        <option value="monthly">Monthly</option>
                    </select>
                </div>
                <div>
                    <h3 className="text-lg font-semibold text-primary-text">First Paycheck Date of a Cycle</h3>
                    <p className="text-secondary-text text-sm mt-1 mb-4">Set the date of any paycheck in your current cycle. This is used as a starting point for automatic calculations.</p>
                    <input type="date" value={nextDate} onChange={e => setNextDate(e.target.value)} className="form-input max-w-xs" />
                </div>
                <PrimaryButton onClick={handleSave}>Save Settings</PrimaryButton>
                <div className="pt-6 border-t border-border-color">
                    <h3 className="text-lg font-semibold text-primary-text">Need a Refresher?</h3>
                    <p className="text-secondary-text text-sm mt-1 mb-4">Review the getting-started guide at any time.</p>
                    {/* This button now calls the onShowIntro function */}
                    <SecondaryButton onClick={onShowIntro}>Show Introduction</SecondaryButton>
                </div>
            </Card>
        </div>
    );
};


// --- Helper Components ---
const ResumeTourButton = ({ onClick }) => (
    <div className="fixed bottom-6 right-6 z-40">
        <PrimaryButton onClick={onClick} className="shadow-lg animate-pulse">
            <StackIcon />
            Resume Tour
        </PrimaryButton>
    </div>
);

const DebtPayoffCalculator = ({ principal, apr, minPayment, extraPayment, onExtraPaymentChange }) => {
    // A simple function to calculate the number of months to payoff a loan.
    // This uses the formula: n = -log(1 - iP/PMT) / log(1 + i)
    const calculatePayoffMonths = (P, i, PMT) => {
        if (PMT <= i * P) {
            return Infinity; // Payment is too low to ever pay off the principal and interest.
        }
        const months = -Math.log(1 - (i * P) / PMT) / Math.log(1 + i);
        return months;
    };

    // Helper to format months into years and months
    const formatDuration = (totalMonths) => {
        if (totalMonths === Infinity) return "Never";
        const years = Math.floor(totalMonths / 12);
        const months = Math.ceil(totalMonths % 12);
        const parts = [];
        if (years > 0) parts.push(`${years} year${years > 1 ? 's' : ''}`);
        if (months > 0) parts.push(`${months} month${months > 1 ? 's' : ''}`);
        return parts.join(', ');
    };

    const monthlyRate = apr / 100 / 12;

    // Calculate months and date with minimum payment
    const monthsWithMinPayment = calculatePayoffMonths(principal, monthlyRate, minPayment);
    const dateWithMinPayment = new Date();
    dateWithMinPayment.setMonth(dateWithMinPayment.getMonth() + monthsWithMinPayment);

    // Calculate months and date with extra payment
    const newPayment = minPayment + extraPayment;
    const monthsWithExtraPayment = calculatePayoffMonths(principal, monthlyRate, newPayment);
    const dateWithExtraPayment = new Date();
    dateWithExtraPayment.setMonth(dateWithExtraPayment.getMonth() + monthsWithExtraPayment);

    // Calculate total interest paid
    const totalInterestMin = (monthsWithMinPayment * minPayment) - principal;
    const totalInterestExtra = (monthsWithExtraPayment * newPayment) - principal;

    const savedMonths = monthsWithMinPayment - monthsWithExtraPayment;
    const savedInterest = totalInterestMin - totalInterestExtra;

    return (
        <div className="mt-4 p-4 bg-off-black rounded-lg">
            <h4 className="text-md font-semibold text-primary-text mb-2">Payoff Projection</h4>
            <div className="space-y-2">
                <div>
                    <p className="text-sm text-secondary-text">Payoff with Min. Payment ({formatCurrency(minPayment)}/mo):</p>
                    <p className="font-bold text-stackd-green">{formatDuration(monthsWithMinPayment)}</p>
                    <p className="text-xs text-secondary-text">
                        Projected Date: {dateWithMinPayment.toLocaleDateString()}
                    </p>
                    <p className="text-xs text-secondary-text">
                        Total Interest: {formatCurrency(totalInterestMin)}
                    </p>
                </div>
                <div>
                    <p className="text-sm text-secondary-text">Add an extra payment to save big:</p>
                    <div className="flex items-center gap-2 mt-1">
                        <CurrencyInput
                            value={extraPayment}
                            onChange={(e) => onExtraPaymentChange(parseFloat(e.target.value))}
                            className="w-28"
                        />
                        <span className="text-secondary-text">/mo</span>
                    </div>
                    {extraPayment > 0 && (
                        <div className="mt-2">
                            <p className="font-bold text-stackd-green">{formatDuration(monthsWithExtraPayment)}</p>
                            <p className="text-xs text-secondary-text">
                                Projected Date: {dateWithExtraPayment.toLocaleDateString()}
                            </p>
                            <p className="text-xs text-secondary-text">
                                Total Interest: {formatCurrency(totalInterestExtra)}
                            </p>
                            <p className="text-sm font-semibold text-stackd-green mt-2">
                                You'll save {formatDuration(savedMonths)} and {formatCurrency(savedInterest)}!
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const DebtGoalItem = ({ goal, payments, onAddPayment, onDelete, onEdit }) => {
    const totalPaid = payments.reduce((sum, p) => sum + p.amount, 0);
    const currentBalance = goal.totalAmount - totalPaid;
    const progress = goal.totalAmount > 0 ? (totalPaid / goal.totalAmount) * 100 : 0;

    // State to manage the extra payment amount
    const [extraPayment, setExtraPayment] = useState(25);
    // New state to toggle the payoff projection visibility
    const [showProjection, setShowProjection] = useState(false);

    // Only show the calculator if the debt is not paid off
    const isPaidOff = currentBalance <= 0;

    // A simple function to calculate the number of months to payoff a loan.
    // This uses the formula: n = -log(1 - iP/PMT) / log(1 + i)
    const calculatePayoffMonths = (P, i, PMT) => {
        if (PMT <= i * P) {
            return Infinity; // Payment is too low to ever pay off the principal and interest.
        }
        const months = -Math.log(1 - (i * P) / PMT) / Math.log(1 + i);
        return months;
    };

    const monthlyRate = goal.interestRate / 100 / 12;
    const monthsWithMinPayment = calculatePayoffMonths(currentBalance, monthlyRate, goal.minPayment);

    // Helper to format months into years and months
    const formatDuration = (totalMonths) => {
        if (totalMonths === Infinity) return "Never";
        const years = Math.floor(totalMonths / 12);
        const months = Math.ceil(totalMonths % 12);
        if (years > 0) {
            return `${years} yr${years > 1 ? 's' : ''}, ${months} mo${months > 1 ? 's' : ''}`;
        }
        return `${months} mo${months > 1 ? 's' : ''}`;
    };

    const formattedDuration = formatDuration(monthsWithMinPayment);

    return (
        <Card className="p-6">
            <div className="flex justify-between items-start">
                <div>
                    <h3 className="text-xl font-bold text-primary-text">{goal.name}</h3>
                    <p className="text-sm text-secondary-text">
                        {goal.interestRate}% APR | Min. Payment: {formatCurrency(goal.minPayment)}
                    </p>
                </div>
                <div className="flex gap-2">
                    <button onClick={onEdit} className="text-secondary-text hover:text-stackd-green"><EditIcon /></button>
                    <button onClick={onDelete} className="text-secondary-text hover:text-error"><TrashIcon /></button>
                </div>
            </div>
            <div className="mt-4">
                <div className="flex justify-between text-sm font-medium text-primary-text mb-1">
                    <span>{formatCurrency(currentBalance)} Remaining</span>
                    <span>{formatCurrency(goal.totalAmount)}</span>
                </div>
                <div className="w-full bg-off-black rounded-full h-4">
                    <div className="bg-stackd-green h-4 rounded-full" style={{ width: `${Math.min(progress, 100)}%` }}></div>
                </div>
                <p className="text-right text-sm text-secondary-text mt-1">{progress.toFixed(1)}% paid off</p>
            </div>

            {/* Conditionally render a summary or the full projection */}
            {!isPaidOff && (
                <div className="mt-4">
                    <button
                        onClick={() => setShowProjection(!showProjection)}
                        className="w-full text-left flex items-center justify-between p-2 rounded-lg text-sm bg-off-black hover:bg-off-black/50 transition-colors"
                    >
                        <span className="font-semibold">Estimated Payoff: {formattedDuration}</span>
                        <span className="text-secondary-text">{showProjection ? 'Hide' : 'Show Details'}</span>
                    </button>
                    {showProjection && (
                        <DebtPayoffCalculator
                            principal={currentBalance}
                            apr={goal.interestRate}
                            minPayment={goal.minPayment}
                            extraPayment={extraPayment}
                            onExtraPaymentChange={setExtraPayment}
                        />
                    )}
                </div>
            )}

            <div className="mt-4 pt-4 border-t border-border-color flex justify-end">
                <PrimaryButton onClick={onAddPayment}>Log Payment</PrimaryButton>
            </div>
        </Card>
    );
};

const SavingsProjectionCalculator = ({ goal, transactions }) => {
    const totalSaved = transactions.reduce((sum, t) => sum + t.amount, 0);
    const monthsRemaining = (goal.targetDate.toDate().getFullYear() - new Date().getFullYear()) * 12 + (goal.targetDate.toDate().getMonth() - new Date().getMonth());
    const remainingAmount = goal.goalAmount - totalSaved;

    const monthlyAvg = transactions.length > 0
        ? transactions.reduce((sum, t) => sum + t.amount, 0) / (
            (new Date().getFullYear() - transactions[transactions.length - 1].date.toDate().getFullYear()) * 12 +
            (new Date().getMonth() - transactions[transactions.length - 1].date.toDate().getMonth()) + 1
        )
        : 0;

    const projectedPayoffMonths = monthlyAvg > 0 ? Math.ceil(remainingAmount / monthlyAvg) : Infinity;
    const projectedPayoffDate = new Date();
    projectedPayoffDate.setMonth(projectedPayoffDate.getMonth() + projectedPayoffMonths);

    const neededMonthly = monthsRemaining > 0 ? remainingAmount / monthsRemaining : remainingAmount;

    return (
        <div className="mt-4 p-4 bg-off-black rounded-lg">
            <h4 className="text-md font-semibold text-primary-text mb-2">Goal Projection</h4>
            <div className="space-y-2">
                <div>
                    <p className="text-sm text-secondary-text">Projected Completion Date (based on your history):</p>
                    <p className="font-bold text-stackd-green">
                        {monthlyAvg > 0 ? projectedPayoffDate.toLocaleDateString() : "No Projection (add contributions)"}
                    </p>
                    {monthlyAvg > 0 && (
                        <p className="text-xs text-secondary-text">
                            Est. {Math.ceil(projectedPayoffMonths)} months
                        </p>
                    )}
                </div>
                <div>
                    <p className="text-sm text-secondary-text">To hit your goal by {goal.targetDate.toDate().toLocaleDateString()}:</p>
                    <p className="font-bold text-primary-text">
                        Save {formatCurrency(neededMonthly)} /mo
                    </p>
                </div>
            </div>
        </div>
    );
};

const AIStrategyAdvisor = ({ debtGoals, geminiModel }) => {
    const [recommendation, setRecommendation] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const generateStrategy = async () => {
        if (!geminiModel || debtGoals.length === 0) {
            setRecommendation("Add at least one debt to get a strategy recommendation.");
            return;
        }
        setIsLoading(true);
        setRecommendation('');
        const debtsList = debtGoals.map(d => `- ${d.name}: $${d.totalAmount.toFixed(2)} at ${d.interestRate}% APR`).join('\n');
        const prompt = `You are a friendly and encouraging financial coach for a budgeting app called STACKD. Your audience is young and new to personal finance. Analyze the user's list of debts and recommend either the "Debt Snowball" or "Debt Avalanche" method. First, briefly explain both methods in simple terms. Then, analyze the user's specific debts provided below and recommend ONE of the two methods. Justify your choice based on their situation. Keep the tone modern, positive, and actionable. Format the output using simple markdown. User's Debts:\n${debtsList}`;
        try {
            const result = await geminiModel.generateContent(prompt);
            setRecommendation(result.response.text());
        } catch (error) {
            console.error("Error generating strategy:", error);
            setRecommendation("Sorry, the AI advisor is having trouble right now. Please try again later.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Card className="p-6 bg-off-black">
            <h3 className="text-lg font-semibold text-primary-text">AI Payoff Strategy</h3>
            <p className="text-secondary-text text-sm mt-1 mb-4">Not sure where to start? Get a personalized recommendation.</p>
            <AiButton onClick={generateStrategy} disabled={isLoading || debtGoals.length === 0}>
                {isLoading ? 'Thinking...' : 'Get AI Recommendation'}
            </AiButton>
            {recommendation && (
                <div className="mt-4 p-4 border border-border-color rounded-lg prose prose-invert max-w-none">
                    <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(marked.parse(recommendation)) }} />
                </div>
            )}
        </Card>
    );
};


// --- Common Components (Refactored) ---
const Header = ({ title, subtitle }) => (
    <header className="mb-8">
        <h1 className="text-4xl sm:text-5xl font-bold text-primary-text tracking-tight">{title}</h1>
        <p className="text-secondary-text mt-2 text-lg">{subtitle}</p>
    </header>
);

const PaycheckSelector = ({ paychecks, selectedPaycheckId, setSelectedPaycheckId, onAddPaycheck, onDeletePaycheck }) => (
    <Card className="p-4 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-3 w-full sm:w-auto">
            <span className="font-semibold text-primary-text">Paycheck:</span>
            <select value={selectedPaycheckId} onChange={(e) => setSelectedPaycheckId(e.target.value)} className="form-input p-2.5" disabled={paychecks.length === 0}>
                {paychecks.length === 0 ? (<option>Add a paycheck to start</option>) : (paychecks.map(p => (<option key={p.id} value={p.id}> {p.name} - {p.date?.seconds ? new Date(p.date.seconds * 1000).toLocaleDateString() : 'Invalid Date'} </option>)))}
            </select>
            {selectedPaycheckId && <button onClick={() => onDeletePaycheck(selectedPaycheckId)} className="text-secondary-text hover:text-error"><TrashIcon /></button>}
        </div>
        <PrimaryButton onClick={onAddPaycheck}>
            <PlusCircleIcon /> Add Paycheck
        </PrimaryButton>
    </Card>
);

const SummaryCard = ({ title, value }) => (
    <Card className="p-6 text-center transition-all duration-300 hover:border-stackd-green/50">
        <h3 className="text-sm font-medium text-secondary-text uppercase tracking-wider">{title}</h3>
        <p className={`text-3xl font-bold mt-2 ${value.includes('-') ? 'text-error' : 'text-stackd-green'}`}>{value}</p>
    </Card>
);

const AllocationMeter = ({ allocated, total }) => {
    const percentage = total > 0 ? (allocated / total) * 100 : 0;
    const isOverAllocated = percentage > 100;
    const displayPercentage = Math.min(percentage, 100); // Cap the bar at 100% for visual consistency

    let progressColor = 'bg-stackd-green';
    if (percentage > 90) progressColor = 'bg-yellow-400';
    if (isOverAllocated) progressColor = 'bg-error';

    return (
        <Card className="p-6">
            <h3 className="text-sm font-medium text-secondary-text uppercase tracking-wider mb-2">Paycheck Allocated</h3>
            <div className="w-full bg-off-black rounded-full h-4 border border-border-color">
                <div
                    className={`${progressColor} h-4 rounded-full transition-all duration-500`}
                    style={{ width: `${displayPercentage}%` }}
                ></div>
            </div>
            <div className="flex justify-between items-baseline mt-2">
                <span className={`text-2xl font-bold ${isOverAllocated ? 'text-error' : 'text-primary-text'}`}>
                    {percentage.toFixed(1)}%
                </span>
                <span className="text-sm text-secondary-text">
                    {formatCurrency(allocated)} of {formatCurrency(total)}
                </span>
            </div>
        </Card>
    );
};

const CategoryItem = ({ category, onAddTransaction, onAllocate, onDelete, onShowDetail }) => {
    const progress = category.allocated > 0 ? (category.spent / category.allocated) * 100 : 0;

    // The main container is now a div to prevent nesting errors
    return (
        <div
            onClick={onShowDetail}
            className="w-full text-left bg-dark-gray p-4 rounded-lg border border-border-color hover:border-white/20 transition-colors duration-200 cursor-pointer"
        >
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex-1 min-w-[150px]">
                    <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-primary-text">{category.name}</h3>
                        {/* This delete button is fine, as it's not a child of another button */}
                        <button
                            onClick={(e) => { e.stopPropagation(); onDelete(); }}
                            className="text-secondary-text hover:text-error text-xs"
                        >
                            &#x2715;
                        </button>
                    </div>
                    <div className="flex items-baseline space-x-2 text-sm text-secondary-text">
                        <span className="font-semibold text-primary-text">{formatCurrency(category.spent)}</span>
                        <span>spent of</span>
                        <span>{formatCurrency(category.allocated)}</span>
                    </div>
                </div>
                <div className="flex-1 min-w-[200px]">
                    <div className="w-full bg-off-black rounded-full h-2.5">
                        <div className="bg-stackd-green h-2.5 rounded-full" style={{ width: `${Math.min(progress, 100)}%` }}></div>
                    </div>
                    <p className="text-right text-sm font-medium text-primary-text mt-1">{formatCurrency(category.remaining)} remaining</p>
                </div>
                <div className="flex gap-2">
                    {/* These buttons are now correctly nested inside a div */}
                    <SecondaryButton onClick={(e) => { e.stopPropagation(); onAllocate(); }} className="py-1 px-3">Allocate</SecondaryButton>
                    <PrimaryButton onClick={(e) => { e.stopPropagation(); onAddTransaction(); }} className="py-1 px-3">+ Spend</PrimaryButton>
                </div>
            </div>
        </div>
    );
};

const Welcome = ({ onAddPaycheck }) => (
    <Card className="text-center p-10 mt-6 flex flex-col items-center">
        <h2 className="text-3xl font-bold text-primary-text mb-4">Welcome to STACKD</h2>
        <p className="text-secondary-text mb-6">Your financial world, simplified. Let's add your first paycheck.</p>
        <PrimaryButton onClick={onAddPaycheck} className="text-lg py-3 px-6">
            Add First Paycheck
        </PrimaryButton>
    </Card>
);

const PremiumGate = ({ onUpgradeClick }) => (
    <Card className="text-center p-10 mt-10">
        <div className="mx-auto bg-stackd-green/10 text-stackd-green rounded-full h-16 w-16 flex items-center justify-center mb-4">
            {/* Updated Icon */}
            <StackIcon />
        </div>
        <h2 className="text-3xl font-bold text-primary-text mb-4">This is a Premium Feature</h2>
        <p className="text-secondary-text mb-6">Upgrade to STACKD Premium to access Advanced Reporting and other exclusive features.</p>
        {/* Centered Button */}
        <div className="flex justify-center">
            <PrimaryButton onClick={onUpgradeClick} className="text-lg py-3 px-6">
                Unlock Premium
            </PrimaryButton>
        </div>
    </Card>
);

// --- Modal Components ---
const Modal = ({ children, onClose, title }) => (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex justify-center items-center z-50 p-4">
        <Card className="shadow-2xl w-full max-w-md">
            <div className="flex justify-between items-center p-4 border-b border-border-color">
                <h3 className="text-xl font-semibold text-primary-text">{title}</h3>
                <button onClick={onClose} className="text-secondary-text hover:text-primary-text text-2xl">&times;</button>
            </div>
            <div className="p-6 max-h-[80vh] overflow-y-auto">
                {children}
            </div>
        </Card>
    </div>
);

const AddPaycheckModal = ({ onClose, onAdd, settings, paychecks, savingsGoals, categories }) => {
    // Paycheck State
    const [name, setName] = useState('');
    const [amount, setAmount] = useState(settings?.defaultPaycheckAmount || '');

    // State to hold the ID of the category used for tracking contributions
    const [savingsCategoryId, setSavingsCategoryId] = useState('');

    // State for goal contributions { goalId: amount }
    const [contributions, setContributions] = useState({});

    // State for the inline new goal form
    const [showNewGoalForm, setShowNewGoalForm] = useState(false);
    const [newGoal, setNewGoal] = useState({ name: '', goalAmount: '', initialContribution: '' });

    // Date calculation logic
    const getNextPaycheckDate = () => {
        const today = new Date();
        if (!settings || settings.paycheckFrequency === 'manual') {
            return today.toISOString().split('T')[0];
        }
        const latestPaycheck = paychecks && paychecks.length > 0 ? paychecks[0] : null;
        let baseDate;
        if (latestPaycheck) {
            baseDate = latestPaycheck.date.toDate();
        } else if (settings.nextPaycheckDate) {
            baseDate = parseDateStringAsLocal(settings.nextPaycheckDate);
        } else {
            return today.toISOString().split('T')[0];
        }
        let nextDate = new Date(baseDate);
        switch (settings.paycheckFrequency) {
            case 'weekly':
                nextDate.setDate(baseDate.getDate() + 7);
                break;
            case 'bi-weekly':
                nextDate.setDate(baseDate.getDate() + 14);
                break;
            case 'monthly':
                nextDate.setMonth(baseDate.getMonth() + 1);
                break;
            default:
                return today.toISOString().split('T')[0];
        }
        return nextDate.toISOString().split('T')[0];
    };
    const [date, setDate] = useState(getNextPaycheckDate);

    // Handlers for updating contributions
    const handleContributionChange = (goalId, value) => {
        const newAmount = parseFloat(value) || 0;
        setContributions(prev => ({ ...prev, [goalId]: newAmount }));
    };

    const handleNewGoalChange = (e) => {
        const { name, value } = e.target;
        setNewGoal(prev => ({ ...prev, [name]: value }));
    };

    // Calculate totals for display
    const totalContributions = Object.values(contributions).reduce((sum, val) => sum + val, 0) + (parseFloat(newGoal.initialContribution) || 0);
    const paycheckAmount = parseFloat(amount) || 0;
    const remainingAmount = paycheckAmount - totalContributions;

    // Main submission handler
    const handleSubmit = (e) => {
        e.preventDefault();
        if (totalContributions > 0 && !savingsCategoryId) {
            alert("Please select a category to track your contributions.");
            return;
        }
        if (!name || !amount || !date || remainingAmount < 0) {
            alert("Please fill out all paycheck details and ensure contributions do not exceed the paycheck amount.");
            return;
        }

        onAdd({
            paycheckData: { name, amount: parseFloat(amount), date: parseDateStringAsLocal(date) },
            contributions: contributions,
            newGoal: showNewGoalForm ? newGoal : null,
            savingsCategoryId: savingsCategoryId
        });
    };

    return (
        <Modal onClose={onClose} title="Log a New Paycheck">
            <form onSubmit={handleSubmit} className="space-y-4">
                {/* --- Paycheck Details --- */}
                <input type="text" placeholder="Paycheck Name" value={name} onChange={e => setName(e.target.value)} className="form-input" required />
                <CurrencyInput placeholder="0.00" value={amount} onChange={e => setAmount(e.target.value)} required />
                <input type="date" value={date} onChange={e => setDate(e.target.value)} className="form-input" required />

                {/* --- Contributions Section --- */}
                <div className="pt-4 mt-4 border-t border-border-color">
                    <h4 className="text-lg font-semibold text-primary-text mb-2">Contribute to Goals (Optional)</h4>
                    <div className="space-y-3 max-h-40 overflow-y-auto pr-2">
                        {savingsGoals.map(goal => (
                            <div key={goal.id} className="flex items-center gap-3">
                                <label htmlFor={goal.id} className="flex-1 text-sm text-secondary-text truncate">{goal.name}</label>
                                <CurrencyInput
                                    id={goal.id}
                                    placeholder="0.00"
                                    value={contributions[goal.id] || ''}
                                    onChange={(e) => handleContributionChange(goal.id, e.target.value)}
                                    className="w-32"
                                />
                            </div>
                        ))}
                        {showNewGoalForm && (
                            <div className="p-3 bg-off-black rounded-lg space-y-2 border border-stackd-green/50">
                                <p className="text-sm font-semibold text-stackd-green">New Goal:</p>
                                <input type="text" name="name" placeholder="New Goal Name" value={newGoal.name} onChange={handleNewGoalChange} className="form-input" required={showNewGoalForm} />
                                <CurrencyInput name="goalAmount" placeholder="Target Amount" value={newGoal.goalAmount} onChange={handleNewGoalChange} required={showNewGoalForm} />
                                <CurrencyInput name="initialContribution" placeholder="Initial Contribution" value={newGoal.initialContribution} onChange={handleNewGoalChange} />
                            </div>
                        )}
                    </div>
                    {!showNewGoalForm && (
                        <SecondaryButton type="button" onClick={() => setShowNewGoalForm(true)} className="w-full mt-3">
                            + Create New Goal
                        </SecondaryButton>
                    )}
                </div>

                {(totalContributions > 0 || showNewGoalForm) && (
                    <div className="pt-4 mt-4 border-t border-border-color">
                        <label htmlFor="savings-category" className="block text-sm font-medium text-secondary-text mb-2">Track Contributions in Category:</label>
                        <select
                            id="savings-category"
                            value={savingsCategoryId}
                            onChange={(e) => setSavingsCategoryId(e.target.value)}
                            className="form-input"
                            required={totalContributions > 0}
                        >
                            <option value="">-- Choose a category --</option>
                            {categories.map(cat => (
                                <option key={cat.id} value={cat.id}>{cat.name}</option>
                            ))}
                        </select>
                    </div>
                )}

                {/* --- Summary & Submission --- */}
                <div className="pt-4 mt-4 border-t border-border-color space-y-2">
                    <div className="flex justify-between text-sm">
                        <span className="text-secondary-text">Paycheck Amount:</span>
                        <span className="font-semibold text-primary-text">{formatCurrency(paycheckAmount)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                        <span className="text-secondary-text">Total Contributions:</span>
                        <span className="font-semibold text-primary-text">-{formatCurrency(totalContributions)}</span>
                    </div>
                    <div className={`flex justify-between text-lg font-bold ${remainingAmount < 0 ? 'text-error' : 'text-stackd-green'}`}>
                        <span>Remaining:</span>
                        <span>{formatCurrency(remainingAmount)}</span>
                    </div>
                </div>

                <PrimaryButton type="submit" className="w-full">Add Paycheck & Contributions</PrimaryButton>
            </form>
        </Modal>
    );
};

const AddTransactionModal = ({ paychecks, categories, onClose, onAdd }) => {
    const [description, setDescription] = useState('');
    const [amount, setAmount] = useState('');
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [categoryId, setCategoryId] = useState('');
    const findPaycheckForDate = (transactionDate, sortedPaychecks) => {
        const tDate = parseDateStringAsLocal(transactionDate);
        for (let i = 0; i < sortedPaychecks.length; i++) {
            const currentPaycheck = sortedPaychecks[i];
            const previousPaycheck = sortedPaychecks[i + 1];
            const pDate = currentPaycheck.date.toDate();
            const periodEndDate = new Date(pDate.getTime());
            const periodStartDate = previousPaycheck ? new Date(previousPaycheck.date.toDate().getTime() + 86400000) : new Date(0);
            if (tDate <= periodEndDate && tDate >= periodStartDate) {
                return currentPaycheck.id;
            }
        }
        return sortedPaychecks.length > 0 ? sortedPaychecks[0].id : null;
    };
    const handleSubmit = (e) => {
        e.preventDefault();
        if (!description || !amount || !date || !categoryId) return;
        const paycheckId = findPaycheckForDate(date, paychecks);
        if (!paycheckId) {
            alert("This date does not fall within any of your paycheck periods. Please adjust the date or add a new paycheck.");
            return;
        }
        onAdd('transactions', { description, amount: parseFloat(amount), date: parseDateStringAsLocal(date), categoryId, paycheckId });
    };
    return (
        <Modal onClose={onClose} title="Log a Transaction">
            <form onSubmit={handleSubmit} className="space-y-4">
                <input type="text" placeholder="Description (e.g., Groceries)" value={description} onChange={e => setDescription(e.target.value)} className="form-input" required />
                <CurrencyInput placeholder="Amount" value={amount} onChange={e => setAmount(e.target.value)} required />
                <input type="date" value={date} onChange={e => setDate(e.target.value)} className="form-input" required />
                <select value={categoryId} onChange={e => setCategoryId(e.target.value)} className="form-input" required>
                    <option value="">Select Category</option>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <PrimaryButton type="submit" className="w-full">Log Transaction</PrimaryButton>
            </form>
        </Modal>
    );
};

const ManageRecurringItemModal = ({ item, type, categories, onClose, onSave, onAddCategory }) => {
    const isDebt = type === 'debt';
    const [formData, setFormData] = useState({
        name: '', amount: '', dueDay: '', categoryId: '', frequency: 'monthly',
        totalAmount: '', interestRate: '', minPayment: '', paymentDay: ''
    });
    const [newCategoryName, setNewCategoryName] = useState('');

    useEffect(() => {
        if (item) {
            setFormData({
                name: item.name || '',
                categoryId: item.categoryId || '',
                frequency: item.frequency || 'monthly',
                amount: item.amount || '',
                dueDay: item.dueDay || '',
                totalAmount: item.totalAmount || '',
                interestRate: item.interestRate || '',
                minPayment: item.minPayment || '',
                paymentDay: item.paymentDay || ''
            });
        }
    }, [item]);

    const handleChange = e => setFormData({ ...formData, [e.target.name]: e.target.value });

    const handleAddNewCategory = async () => {
        if (!newCategoryName.trim()) return;
        const docRef = await onAddCategory(newCategoryName.trim());
        if (docRef) {
            setFormData({ ...formData, categoryId: docRef.id });
            setNewCategoryName('');
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        let payload;
        if (isDebt) {
            if (!formData.name || !formData.totalAmount || !formData.interestRate || !formData.minPayment || !formData.paymentDay || !formData.categoryId) return;
            payload = { name: formData.name, totalAmount: parseFloat(formData.totalAmount), interestRate: parseFloat(formData.interestRate), minPayment: parseFloat(formData.minPayment), paymentDay: parseInt(formData.paymentDay), frequency: formData.frequency, categoryId: formData.categoryId };
            onSave('debtGoals', item ? item.id : null, payload);
        } else {
            if (!formData.name || !formData.amount || !formData.dueDay || !formData.categoryId) return;
            payload = { name: formData.name, amount: parseFloat(formData.amount), dueDay: parseInt(formData.dueDay), frequency: formData.frequency, categoryId: formData.categoryId };
            onSave('monthlyBills', item ? item.id : null, payload);
        }
        onClose();
    };

    return (
        <Modal onClose={onClose} title={item ? `Edit ${isDebt ? 'Debt' : 'Bill'}` : `Add New ${isDebt ? 'Debt' : 'Bill'}`}>
            <form onSubmit={handleSubmit} className="space-y-4">
                <input type="text" name="name" placeholder={isDebt ? 'Debt Name (e.g., Visa Card)' : 'Bill Name'} value={formData.name} onChange={handleChange} className="form-input" required />
                {isDebt ? (
                    <>
                        <CurrencyInput name="totalAmount" placeholder="Total Amount Owed" value={formData.totalAmount} onChange={handleChange} required />
                        <PercentageInput name="interestRate" placeholder="Interest Rate (APR)" value={formData.interestRate} onChange={handleChange} required />
                        <CurrencyInput name="minPayment" placeholder="Minimum Monthly Payment" value={formData.minPayment} onChange={handleChange} required />
                        <input type="number" name="paymentDay" placeholder="Payment Day (1-31)" value={formData.paymentDay} onChange={handleChange} className="form-input" required min="1" max="31" />
                    </>
                ) : (
                    <>
                        <CurrencyInput name="amount" placeholder="Amount" value={formData.amount} onChange={handleChange} required />
                        <input type="number" name="dueDay" placeholder="Due Day (1-31)" value={formData.dueDay} onChange={handleChange} className="form-input" required min="1" max="31" />
                    </>
                )}
                <select name="frequency" value={formData.frequency} onChange={handleChange} className="form-input" required>
                    <option value="monthly">Monthly</option>
                    <option value="bi-weekly">Bi-Weekly</option>
                    <option value="weekly">Weekly</option>
                    {!isDebt && <option value="one-time">One-Time</option>}
                </select>
                <select name="categoryId" value={formData.categoryId} onChange={handleChange} className="form-input" required>
                    <option value="">Select Category</option>
                    {categories.sort((a, b) => a.name.localeCompare(b.name)).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    <option value="--create--">-- Create New Category --</option>
                </select>
                {formData.categoryId === '--create--' && (
                    <div className="flex gap-2 items-center">
                        <input type="text" placeholder="New Category Name" value={newCategoryName} onChange={e => setNewCategoryName(e.target.value)} className="form-input" />
                        <PrimaryButton type="button" onClick={handleAddNewCategory}>Add</PrimaryButton>
                    </div>
                )}
                <PrimaryButton type="submit" className="w-full">Save</PrimaryButton>
            </form>
        </Modal>
    );
};

const handleAddPaycheckWithContributions = async (data, db, user, categories, savingsGoals, setModal, budgetItems) => {
    const { paycheckData, contributions, newGoal, savingsCategoryId } = data;
    if (!db || !user) return;

    // The check for an empty savingsCategoryId is now handled inside the modal before this function is called.

    const batch = writeBatch(db);
    let newPaycheckId = null;

    try {
        const paycheckRef = doc(collection(db, `/artifacts/${appId}/users/${user.uid}/paychecks`));
        newPaycheckId = paycheckRef.id;
        batch.set(paycheckRef, { ...paycheckData, userId: user.uid });

        let newGoalId = null;
        let totalContributions = 0;

        if (newGoal && newGoal.name && newGoal.goalAmount) {
            const newGoalRef = doc(collection(db, `/artifacts/${appId}/users/${user.uid}/savingsGoals`));
            newGoalId = newGoalRef.id;
            batch.set(newGoalRef, {
                name: newGoal.name,
                goalAmount: parseFloat(newGoal.goalAmount),
                targetDate: new Date(), // Using a default date, can be enhanced later
                userId: user.uid
            });
            if (newGoal.initialContribution > 0) {
                const initialContributionAmount = parseFloat(newGoal.initialContribution);
                totalContributions += initialContributionAmount;
                const newGoalContributionRef = doc(collection(db, `/artifacts/${appId}/users/${user.uid}/savingsTransactions`));
                batch.set(newGoalContributionRef, {
                    goalId: newGoalId,
                    amount: initialContributionAmount,
                    date: paycheckData.date,
                    userId: user.uid
                });
                const generalTxRef = doc(collection(db, `/artifacts/${appId}/users/${user.uid}/transactions`));
                batch.set(generalTxRef, {
                    description: `Contribution to: ${newGoal.name}`,
                    amount: initialContributionAmount,
                    date: paycheckData.date,
                    categoryId: savingsCategoryId,
                    paycheckId: newPaycheckId,
                    userId: user.uid
                });
            }
        }

        for (const goalId in contributions) {
            const amount = contributions[goalId];
            if (amount > 0) {
                totalContributions += amount;
                const goalName = savingsGoals.find(g => g.id === goalId)?.name || 'Goal';
                // Create the savings-specific transaction
                const savingsTxRef = doc(collection(db, `/artifacts/${appId}/users/${user.uid}/savingsTransactions`));
                batch.set(savingsTxRef, {
                    goalId,
                    amount,
                    date: paycheckData.date,
                    userId: user.uid
                });

                // Create a matching general transaction to be budgeted
                const generalTxRef = doc(collection(db, `/artifacts/${appId}/users/${user.uid}/transactions`));
                batch.set(generalTxRef, {
                    description: `Contribution to: ${goalName}`,
                    amount,
                    date: paycheckData.date,
                    categoryId: savingsCategoryId,
                    paycheckId: newPaycheckId,
                    userId: user.uid
                });
            }
        }

        // FIX: Create or update the budgetItem for the savings category
        if (totalContributions > 0 && savingsCategoryId) {
            const budgetItemsRef = collection(db, `/artifacts/${appId}/users/${user.uid}/budgetItems`);
            const q = query(budgetItemsRef, where("paycheckId", "==", newPaycheckId), where("categoryId", "==", savingsCategoryId));
            const querySnapshot = await getDocs(q);

            if (!querySnapshot.empty) {
                // Budget item exists, update it
                const existingDoc = querySnapshot.docs[0];
                const newAllocated = (existingDoc.data().allocated || 0) + totalContributions;
                batch.update(existingDoc.ref, { allocated: newAllocated });
            } else {
                // Budget item does not exist, create it
                const newBudgetItemRef = doc(budgetItemsRef);
                batch.set(newBudgetItemRef, {
                    paycheckId: newPaycheckId,
                    categoryId: savingsCategoryId,
                    allocated: totalContributions,
                    userId: user.uid
                });
            }
        }


        await batch.commit();
        setModal(null);
    } catch (error) {
        console.error("Error adding paycheck with contributions: ", error);
        alert("There was an error saving your paycheck and contributions. Please try again.");
    }
};

const EditTransactionModal = ({ transaction, paychecks, categories, handleUpdate, onClose }) => {
    const [description, setDescription] = useState(transaction.description || '');
    const [amount, setAmount] = useState(transaction.amount || '');
    const [date, setDate] = useState(transaction.date?.seconds ? new Date(transaction.date.seconds * 1000).toISOString().split('T')[0] : '');
    const [categoryId, setCategoryId] = useState(transaction.categoryId || '');
    const [paycheckId, setPaycheckId] = useState(transaction.paycheckId || '');
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!description || !amount || !date || !categoryId) return;
        await handleUpdate('transactions', transaction.id, { description, amount: parseFloat(amount), date: parseDateStringAsLocal(date), categoryId, paycheckId });
        onClose();
    };
    return (
        <Modal onClose={onClose} title="Edit Transaction">
            <form onSubmit={handleSubmit} className="space-y-4">
                <input type="text" placeholder="Description" value={description} onChange={e => setDescription(e.target.value)} className="form-input" required />
                <CurrencyInput placeholder="Amount" value={amount} onChange={e => setAmount(e.target.value)} required />
                <input type="date" value={date} onChange={e => setDate(e.target.value)} className="form-input" required />
                <select value={categoryId} onChange={e => setCategoryId(e.target.value)} className="form-input" required>
                    <option value="">Select Category</option>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <select value={paycheckId} onChange={e => setPaycheckId(e.target.value)} className="form-input">
                    <option value="">Assign to Paycheck</option>
                    {paychecks.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <PrimaryButton type="submit" className="w-full">Save Changes</PrimaryButton>
            </form>
        </Modal>
    );
};

const CategoryDetailModal = ({ category, onClose, onEditTransaction, handleDelete }) => {
    return (
        <Modal onClose={onClose} title={`Details for ${category.name}`}>
            <div className="space-y-2">
                {category.transactions.length > 0 ? category.transactions.map(t => (
                    <div key={t.id} className="flex items-center justify-between p-3 bg-off-black rounded-lg">
                        <div className="flex-1">
                            <p className="text-primary-text">{t.description}</p>
                            <p className="text-xs text-secondary-text">
                                {t.date?.seconds ? new Date(t.date.seconds * 1000).toLocaleDateString() : 'No Date'}
                            </p>
                        </div>
                        <div className="flex items-center gap-4">
                            <p className="font-semibold text-primary-text">{formatCurrency(t.amount)}</p>
                            <button onClick={() => onEditTransaction(t)} className="text-secondary-text hover:text-warning transition-colors"><EditIcon /></button>
                            <button onClick={() => handleDelete('transactions', t.id)} className="text-secondary-text hover:text-error transition-colors"><TrashIcon /></button>
                        </div>
                    </div>
                )) : <p className="text-secondary-text text-center py-4">No transactions for this category in this period.</p>}
            </div>
        </Modal>
    );
};

const AddCategoryModal = ({ onClose, onAdd }) => {
    const [name, setName] = useState('');
    const handleSubmit = (e) => { e.preventDefault(); if (!name) return; onAdd('categories', { name }); };
    return (<Modal onClose={onClose} title="Add New Category"> <form onSubmit={handleSubmit} className="space-y-4"> <input type="text" placeholder="Category Name (e.g., Groceries)" value={name} onChange={e => setName(e.target.value)} className="form-input" required /> <PrimaryButton type="submit" className="w-full">Add Category</PrimaryButton> </form> </Modal>);
};

const AllocationModal = ({ category, unallocated, onClose, onAllocate }) => {
    const [amount, setAmount] = useState(category.allocated || '');
    const handleSubmit = (e) => { e.preventDefault(); onAllocate({ categoryId: category.id, amount: parseFloat(amount || 0) }); };
    const totalUnallocated = unallocated + (category.allocated || 0);
    return (<Modal onClose={onClose} title={`Allocate for ${category.name}`}> <form onSubmit={handleSubmit} className="space-y-4"> <p className="text-center text-secondary-text"> Unallocated: <span className="font-bold text-stackd-green">{formatCurrency(totalUnallocated)}</span> </p> <CurrencyInput placeholder="Amount to Allocate" value={amount} onChange={e => setAmount(e.target.value)} className="text-center text-2xl" required max={totalUnallocated} /> <PrimaryButton type="submit" className="w-full">Set Allocation</PrimaryButton> </form> </Modal>);
};

const ConfirmModal = ({ message, onResult }) => (<Modal onClose={() => onResult(false)} title="Please Confirm"> <div className="text-center"> <p className="text-primary-text mb-6">{message}</p> <div className="flex justify-center gap-4"> <SecondaryButton onClick={() => onResult(false)}>Cancel</SecondaryButton> <button onClick={() => onResult(true)} className="bg-error hover:opacity-90 text-primary-text font-medium uppercase tracking-wider text-sm py-2.5 px-5 rounded-lg">Confirm Delete</button> </div> </div> </Modal>);

const AnalyzeReceiptModal = ({ categories, paychecks, onAdd, onClose, geminiModel }) => {
    const [receiptImage, setReceiptImage] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [analysisResult, setAnalysisResult] = useState(null);
    const [error, setError] = useState('');
    const [showCreateCategoryPrompt, setShowCreateCategoryPrompt] = useState(false);
    const [selectedPaycheckId, setSelectedPaycheckId] = useState('');

    // NEW: State to hold the date entered by the user.
    const [manualDate, setManualDate] = useState('');

    useEffect(() => {
        // Default to the most recent paycheck when the modal opens.
        if (paychecks && paychecks.length > 0) {
            setSelectedPaycheckId(paychecks[0].id);
        }
    }, [paychecks]);

    // NEW: Effect to set a default date (today) after AI analysis is complete.
    useEffect(() => {
        if (analysisResult) {
            setManualDate(new Date().toISOString().split('T')[0]);
        }
    }, [analysisResult]);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setReceiptImage(reader.result);
                setAnalysisResult(null);
                setError('');
                setShowCreateCategoryPrompt(false);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleAnalyze = async () => {
        if (!receiptImage || !geminiModel) {
            setError("AI model not ready or no image selected.");
            return;
        }
        setIsLoading(true);
        setError('');
        const base64Data = receiptImage.split(',')[1];

        // UPDATED: The prompt now uses the user's dynamic category list and no longer asks for the date.
        const categoryList = categories.map(c => c.name).join(', ');
        const prompt = `Analyze the attached receipt image. Extract the merchant name and the total amount. Suggest the most appropriate category for this expense from the following list: ${categoryList}. If no category seems to fit, suggest "Miscellaneous". Return the result as a JSON object.`;

        // UPDATED: The JSON schema no longer includes a 'date' field.
        const schema = {
            type: "OBJECT",
            properties: {
                "merchant": { "type": "STRING" },
                "amount": { "type": "NUMBER" },
                "category": { "type": "STRING" }
            }
        };

        try {
            const parts = [{ text: prompt }, { inlineData: { mimeType: "image/jpeg", data: base64Data } }];
            const result = await geminiModel.generateContent({
                contents: [{ role: 'user', parts }],
                generationConfig: {
                    responseMimeType: "application/json",
                    responseSchema: schema,
                },
            });
            const responseText = result.response.text();
            setAnalysisResult(JSON.parse(responseText));
        } catch (err) {
            setError("Sorry, I couldn't analyze the receipt. Please try again or enter it manually.");
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    // Shared submission logic to avoid repetition.
    const executeAddTransaction = (categoryId) => {
        if (!manualDate || !selectedPaycheckId) {
            setError("Please select a date and a paycheck.");
            return;
        }
        onAdd('transactions', {
            description: analysisResult.merchant,
            amount: analysisResult.amount,
            date: parseDateStringAsLocal(manualDate), // Use the manually entered date
            categoryId: categoryId,
            paycheckId: selectedPaycheckId
        });
        onClose();
    };

    const handleSubmit = () => {
        if (!analysisResult) return;
        let category = categories.find(c => c.name.toLowerCase() === analysisResult.category.toLowerCase());
        if (!category) {
            setError('');
            setShowCreateCategoryPrompt(true);
            return;
        }
        executeAddTransaction(category.id);
    };

    const handleCreateAndSubmit = async () => {
        if (!analysisResult) return;
        const newCategoryRef = await onAdd('categories', { name: analysisResult.category }, false);
        if (newCategoryRef) {
            executeAddTransaction(newCategoryRef.id);
        }
    };

    return (
        <Modal onClose={onClose} title="Scan Receipt">
            {!analysisResult ? (
                <div className="space-y-4">
                    <p className="text-secondary-text">Upload a receipt and the AI will automatically extract the details.</p>
                    <input type="file" accept="image/*" onChange={handleFileChange} className="block w-full text-sm text-secondary-text file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-stackd-green/10 file:text-stackd-green hover:file:bg-stackd-green/20" />
                    {receiptImage && <img src={receiptImage} alt="Receipt preview" className="mt-2 rounded-lg max-h-60 mx-auto" />}
                    <PrimaryButton onClick={handleAnalyze} disabled={isLoading || !receiptImage} className="w-full">
                        {isLoading ? 'Analyzing...' : 'Analyze Receipt'}
                    </PrimaryButton>
                    {error && <p className="text-error text-sm text-center">{error}</p>}
                </div>
            ) : (
                <div className="space-y-4">
                    <h4 className="text-lg font-semibold text-primary-text">Confirm Transaction Details</h4>
                    <div className="bg-off-black p-4 rounded-lg space-y-2">
                        <p><strong>Merchant:</strong> {analysisResult.merchant}</p>
                        <p><strong>Amount:</strong> {formatCurrency(analysisResult.amount)}</p>
                        <p><strong>Suggested Category:</strong> {analysisResult.category}</p>
                    </div>

                    {/* NEW: Manual date input field, appears after analysis. */}
                    <div>
                        <label htmlFor="transaction-date" className="block text-sm font-medium text-secondary-text mb-1">Transaction Date:</label>
                        <input
                            type="date"
                            id="transaction-date"
                            value={manualDate}
                            onChange={(e) => setManualDate(e.target.value)}
                            className="form-input"
                            required
                        />
                    </div>

                    {/* Paycheck selector dropdown */}
                    <div>
                        <label htmlFor="paycheck-select" className="block text-sm font-medium text-secondary-text mb-1">Assign to Paycheck:</label>
                        <select
                            id="paycheck-select"
                            value={selectedPaycheckId}
                            onChange={(e) => setSelectedPaycheckId(e.target.value)}
                            className="form-input"
                        >
                            {paychecks.length === 0 ? (
                                <option>No paychecks available</option>
                            ) : (
                                paychecks.map(p => (
                                    <option key={p.id} value={p.id}>
                                        {p.name} - {p.date?.seconds ? new Date(p.date.seconds * 1000).toLocaleDateString() : ''}
                                    </option>
                                ))
                            )}
                        </select>
                    </div>

                    {showCreateCategoryPrompt ? (
                        <div className="bg-warning/10 border border-warning/30 p-4 rounded-lg text-center">
                            <p className="font-semibold text-warning">Category Not Found</p>
                            <p className="text-sm text-warning/80 mb-4">The category "{analysisResult.category}" doesn't exist. Would you like to create it?</p>
                            <div className="flex gap-4">
                                <SecondaryButton onClick={() => setShowCreateCategoryPrompt(false)} className="w-full">Cancel</SecondaryButton>
                                <PrimaryButton onClick={handleCreateAndSubmit} className="w-full" disabled={!selectedPaycheckId || !manualDate}>Create & Add</PrimaryButton>
                            </div>
                        </div>
                    ) : (
                        <div className="flex gap-4 pt-2">
                            <SecondaryButton onClick={() => { setAnalysisResult(null); setReceiptImage(null); }} className="w-full">Back</SecondaryButton>
                            <PrimaryButton onClick={handleSubmit} className="w-full" disabled={!selectedPaycheckId || !manualDate}>Add Expense</PrimaryButton>
                        </div>
                    )}
                    {error && <p className="text-error text-sm text-center mt-2">{error}</p>}
                </div>
            )}
        </Modal>
    );
};

const OnboardingModal = ({ onboarding, setOnboarding, setActiveView, setShowOnboarding }) => {
    const { step } = onboarding;

    // Ends the tour and resets the state
    const handleEndTour = () => {
        setOnboarding({ inProgress: false, step: 0 });
        setShowOnboarding(false);
    };

    // Navigates to a new view and hides the modal
    const handleAction = (view, nextStep) => {
        setActiveView(view);
        setOnboarding(prev => ({ ...prev, step: nextStep }));
        setShowOnboarding(false);
    };

    const steps = [
        {
            icon: <StackIcon />,
            title: "Ready to Stack Up?",
            content: "Welcome to STACKD! We're about to turn your financial chaos into a clean, powerful stack. This quick tour will show you the ropes.",
            buttonText: "Let's Build",
            action: () => setOnboarding(prev => ({ ...prev, step: 1 })),
        },
        {
            icon: <GuestIcon />,
            title: "First, Secure Your Stack",
            content: "The best way to use STACKD is with a free account. It saves your progress securely and lets you access your budget from any device. You can sign up anytime from the profile in the bottom-left.",
            buttonText: "Got It, Next!",
            action: () => setOnboarding(prev => ({ ...prev, step: 2 })),
        },
        {
            icon: <SettingsIcon />,
            title: "Sync Up With Your Life",
            content: "Head to Settings to match the app to your pay schedule. We move to your beat—when you get paid, our app works the way that you do. You can also re-launch this tour from here anytime.",
            buttonText: "Go to Settings",
            action: () => handleAction('settings', 3),
        },
        {
            icon: <PlusCircleIcon />,
            title: "Lay the Foundation",
            content: "Your paycheck is the foundation of your entire financial stack. Let's log your income—this is the money we'll put to work.",
            buttonText: "Add a Paycheck",
            action: () => handleAction('budget', 4),
        },
        {
            icon: <EditIcon />,
            title: "Create Your Buckets",
            content: "On the Budget page, you can edit your categories. Think of these as the buckets your money will flow into. Customize them to fit your lifestyle.",
            buttonText: "I'll Do That, Next!",
            action: () => setOnboarding(prev => ({ ...prev, step: 5 })),
        },
        {
            icon: <BillsIcon />,
            title: "Automate Your Essentials",
            content: "Now, let's get your recurring bills out of the way. Adding them to the 'Bills' page gives you a clear view of your fixed costs so there are no surprises.",
            buttonText: "Go to Bills",
            action: () => handleAction('bills', 6),
        },
        {
            icon: <DebtIcon />,
            title: "Map Your Path to Freedom",
            content: "Time to face the final boss: debt. Listing your debts on the 'Debt' page is the first step to crushing them. Let's make a plan.",
            buttonText: "Go to Debt",
            action: () => handleAction('debt', 7),
        },
        {
            icon: <SavingsIcon />,
            title: "Build Your Future",
            content: "This is the fun part. What are you stacking for? A vacation, a down payment, an emergency fund? Adding goals gives your money a powerful purpose beyond just paying bills.",
            buttonText: "Go to Goals",
            action: () => handleAction('savings', 8),
        },
        {
            icon: <StackIcon />,
            title: "Unlock Your AI Co-Pilot",
            content: "You've built the foundation. Now, supercharge it. Create an account and go Premium to unlock personalized AI analysis, receipt scanning, and advanced reports.",
            buttonText: "Finish Tour",
            action: handleEndTour,
        }
    ];

    const currentStep = steps[step];
    if (!currentStep) return null;

    return (
        <Modal onClose={handleEndTour} title={`Step ${step + 1} of ${steps.length}`}>
            <div className="text-center">
                <div className="mx-auto bg-stackd-green/10 text-stackd-green rounded-full h-16 w-16 flex items-center justify-center mb-4">{currentStep.icon}</div>
                <h3 className="text-2xl font-bold text-primary-text">{currentStep.title}</h3>
                <p className="text-secondary-text mt-2 mb-8">{currentStep.content}</p>
                <div className="flex items-center justify-between">
                    <SecondaryButton onClick={handleEndTour}>Skip Tour</SecondaryButton>
                    <PrimaryButton onClick={currentStep.action}>{currentStep.buttonText}</PrimaryButton>
                </div>
            </div>
        </Modal>
    );
};

const UpgradeModal = ({ onClose, onUpgrade }) => {
    return (
        <Modal onClose={onClose} title="Go Premium">
            <div className="text-center">
                <div className="mx-auto bg-stackd-green/10 text-stackd-green rounded-full h-16 w-16 flex items-center justify-center mb-4"> <SparklesIcon /> </div>
                <h3 className="text-2xl font-bold text-primary-text">Unlock Your Full Potential</h3>
                <p className="text-secondary-text mt-2 mb-6">Upgrade to STACKD Premium to accelerate your financial goals.</p>
                <ul className="text-left space-y-3 bg-off-black p-4 rounded-lg mb-6">
                    <li className="flex items-center gap-3"><span className="text-stackd-green">✓</span> <span>AI-Powered Strategy Advisors</span></li>
                    <li className="flex items-center gap-3"><span className="text-stackd-green">✓</span> <span>AI Receipt Scanning</span></li>
                    <li className="flex items-center gap-3"><span className="text-stackd-green">✓</span> <span>Advanced Reporting & Insights</span></li>
                    <li className="flex items-center gap-3"><span className="text-stackd-green">★</span> <span>Early access to new features</span></li>
                </ul>
                <PrimaryButton onClick={onUpgrade} className="w-full text-lg py-3">Upgrade for $4.99/mo</PrimaryButton>
            </div>
        </Modal>
    );
};

const PremiumLock = ({ onClick }) => (
    // Changed from rounded-xl to rounded-lg to match the button style
    <div onClick={onClick} className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center rounded-lg cursor-pointer backdrop-blur-sm">
        <StackIcon className="text-stackd-green" />
        <h4 className="font-bold text-lg text-stackd-green mt-2">Premium Feature</h4>
        <p className="text-primary-text text-sm">Click to Unlock</p>
    </div>
);

const AddSavingsGoalModal = ({ onClose, onAdd }) => { const [name, setName] = useState(''); const [amount, setAmount] = useState(''); const [targetDate, setTargetDate] = useState(''); const handleSubmit = (e) => { e.preventDefault(); if (!name || !amount || !targetDate) return; onAdd('savingsGoals', { name, goalAmount: parseFloat(amount), targetDate: parseDateStringAsLocal(targetDate) }); }; return (<Modal onClose={onClose} title="New Savings Goal"> <form onSubmit={handleSubmit} className="space-y-4"> <input type="text" placeholder="Goal Name (e.g., Vacation)" value={name} onChange={e => setName(e.target.value)} className="form-input" required /> <CurrencyInput placeholder="Goal Amount" value={amount} onChange={e => setAmount(e.target.value)} required /> <input type="date" value={targetDate} onChange={e => setTargetDate(e.target.value)} className="form-input" required /> <PrimaryButton type="submit" className="w-full">Add Goal</PrimaryButton> </form> </Modal>); };
const GoalDetailModal = ({ goalData, onClose, onAddContribution, onMakeWithdrawal, onEditTransaction, onDeleteTransaction }) => {
    const { goal, transactions } = goalData;
    const totalSaved = transactions.reduce((sum, t) => sum + t.amount, 0);

    return (
        <Modal onClose={onClose} title={goal.name}>
            <div className="space-y-4">
                <div>
                    <div className="flex justify-between text-sm font-medium text-primary-text mb-1">
                        <span>{formatCurrency(totalSaved)}</span>
                        <span>{formatCurrency(goal.goalAmount)}</span>
                    </div>
                    <div className="w-full bg-off-black rounded-full h-4">
                        <div className="bg-stackd-green h-4 rounded-full" style={{ width: `${Math.min((totalSaved / goal.goalAmount) * 100, 100)}%` }}></div>
                    </div>
                    <p className="text-right text-sm text-secondary-text mt-1">{((totalSaved / goal.goalAmount) * 100).toFixed(1)}% complete</p>
                </div>

                {/* Add the new projection calculator here */}
                <SavingsProjectionCalculator goal={goal} transactions={transactions} />

                <div className="flex gap-4">
                    <PrimaryButton onClick={() => onAddContribution(goal)} className="w-full">Add Contribution</PrimaryButton>
                    <SecondaryButton onClick={() => onMakeWithdrawal(goal)} className="w-full">Make Withdrawal</SecondaryButton>
                </div>
                <div className="pt-4 border-t border-border-color">
                    <h4 className="text-lg font-semibold text-primary-text mb-2">History</h4>
                    <div className="space-y-2 max-h-60 overflow-y-auto pr-2">
                        {transactions.length > 0 ? [...transactions].sort((a, b) => b.date.seconds - a.date.seconds).map(tx => (
                            <div key={tx.id} className="flex items-center justify-between p-3 bg-off-black rounded-lg">
                                <div>
                                    <p className={`font-semibold ${tx.amount >= 0 ? 'text-stackd-green' : 'text-error'}`}>
                                        {tx.amount >= 0 ? 'Contribution' : 'Withdrawal'}
                                    </p>
                                    <p className="text-xs text-secondary-text">{new Date(tx.date.seconds * 1000).toLocaleDateString()}</p>
                                </div>
                                <div className="flex items-center gap-3">
                                    <p className="font-semibold text-primary-text">{formatCurrency(Math.abs(tx.amount))}</p>
                                    <button onClick={() => onEditTransaction(tx)} className="text-secondary-text hover:text-warning"><EditIcon /></button>
                                    <button onClick={() => onDeleteTransaction(tx.id)} className="text-secondary-text hover:text-error"><TrashIcon /></button>
                                </div>
                            </div>
                        )) : (
                            <p className="text-center text-secondary-text py-4">No transactions yet.</p>
                        )}
                    </div>
                </div>
            </div>
        </Modal>
    );
};

// Replace the existing AddContributionModal with this modified version
const AddContributionModal = ({ goal, onClose, onAdd, isWithdrawal = false }) => {
    const [amount, setAmount] = useState('');
    const title = isWithdrawal ? `Withdraw from ${goal.name}` : `Contribute to ${goal.name}`;
    const buttonLabel = isWithdrawal ? 'Confirm Withdrawal' : 'Add Contribution';

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!amount || parseFloat(amount) <= 0) return;

        const finalAmount = isWithdrawal ? -Math.abs(parseFloat(amount)) : parseFloat(amount);

        // This still uses the generic 'handleAdd' from App.js
        onAdd('savingsTransactions', {
            goalId: goal.id,
            amount: finalAmount,
            date: new Date()
        });
    };

    return (
        <Modal onClose={onClose} title={title}>
            <form onSubmit={handleSubmit} className="space-y-4">
                <CurrencyInput
                    placeholder="Amount"
                    value={amount}
                    onChange={e => setAmount(e.target.value)}
                    required
                />
                <PrimaryButton type="submit" className="w-full">{buttonLabel}</PrimaryButton>
            </form>
        </Modal>
    );
};

// Add this new component
const EditSavingsTransactionModal = ({ transaction, onClose, onUpdate }) => {
    const [amount, setAmount] = useState(Math.abs(transaction.amount) || '');
    const [date, setDate] = useState(transaction.date?.seconds ? new Date(transaction.date.seconds * 1000).toISOString().split('T')[0] : '');
    const isWithdrawal = transaction.amount < 0;

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!amount || !date || parseFloat(amount) <= 0) return;

        const finalAmount = isWithdrawal ? -Math.abs(parseFloat(amount)) : parseFloat(amount);
        onUpdate('savingsTransactions', transaction.id, {
            amount: finalAmount,
            date: parseDateStringAsLocal(date)
        });
    };

    return (
        <Modal onClose={onClose} title="Edit Transaction">
            <form onSubmit={handleSubmit} className="space-y-4">
                <CurrencyInput value={amount} onChange={e => setAmount(e.target.value)} required />
                <input type="date" value={date} onChange={e => setDate(e.target.value)} className="form-input" required />
                <PrimaryButton type="submit" className="w-full">Save Changes</PrimaryButton>
            </form>
        </Modal>
    );
};
const AuthModal = ({ onGoogleSignIn, onClose, error }) => {
    return (
        <Modal onClose={onClose} title="Sign Up or Log In">
            <div className="text-center">
                <p className="text-secondary-text mb-6">
                    Create a free account or log in to save your progress and access your budget on any device.
                </p>
                <button
                    onClick={onGoogleSignIn}
                    className="w-full flex items-center justify-center gap-3 bg-primary-text text-off-black font-semibold py-3 px-4 rounded-lg transition hover:bg-white/90 shadow"
                >
                    <GoogleIcon />
                    Continue with Google
                </button>
                {error && <p className="text-error text-sm mt-4">{error}</p>}
                <p className="text-xs text-secondary-text mt-6">
                    By continuing, you agree to the Stackd Terms of Service.
                </p>
            </div>
        </Modal>
    );
};

const AddDebtPaymentModal = ({ goal, onClose, onAdd }) => {
    const [amount, setAmount] = useState('');
    const handleSubmit = (e) => { e.preventDefault(); if (!amount) return; onAdd('debtPayments', { goalId: goal.id, amount: parseFloat(amount), date: new Date() }); };
    return (
        <Modal onClose={onClose} title={`Log Payment for ${goal.name}`}>
            <form onSubmit={handleSubmit} className="space-y-4">
                <CurrencyInput placeholder="Payment Amount" value={amount} onChange={e => setAmount(e.target.value)} required />
                <PrimaryButton type="submit" className="w-full">Log Payment</PrimaryButton>
            </form>
        </Modal>
    );
};

const SavingsGoalItem = ({ goal, transactions, onDelete, onShowDetail }) => {
    const totalSaved = transactions.reduce((sum, t) => sum + t.amount, 0);
    const progress = goal.goalAmount > 0 ? (totalSaved / goal.goalAmount) * 100 : 0;

    // We'll calculate the projection here for the preview text
    const monthsToGo = (goal.targetDate.toDate().getFullYear() - new Date().getFullYear()) * 12 + (goal.targetDate.toDate().getMonth() - new Date().getMonth());
    const monthlyNeeded = monthsToGo > 0 ? (goal.goalAmount - totalSaved) / monthsToGo : (goal.goalAmount - totalSaved);

    return (
        <button onClick={onShowDetail} className="w-full text-left">
            <Card className="p-6 transition-colors hover:border-stackd-green/50">
                <div className="flex justify-between items-start">
                    <div>
                        <h3 className="text-xl font-bold text-primary-text">{goal.name}</h3>
                        <p className="text-secondary-text">Target: {formatCurrency(goal.goalAmount)}</p>
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="text-secondary-text hover:text-error"><TrashIcon /></button>
                </div>
                <div className="mt-4">
                    <div className="flex justify-between text-sm font-medium text-primary-text mb-1">
                        <span>Progress</span>
                        <span>{formatCurrency(totalSaved)}</span>
                    </div>
                    <div className="w-full bg-off-black rounded-full h-4">
                        <div className="bg-stackd-green h-4 rounded-full" style={{ width: `${Math.min(progress, 100)}%` }}></div>
                    </div>
                    <p className="text-right text-sm text-secondary-text mt-1">{progress.toFixed(1)}% complete</p>
                </div>

                {/* New Preview Section */}
                {totalSaved < goal.goalAmount && (
                    <div className="mt-4 pt-4 border-t border-border-color">
                        <p className="text-sm text-secondary-text">
                            You need to save <strong>{formatCurrency(monthlyNeeded)}</strong> per month to hit your target date.
                        </p>
                    </div>
                )}
            </Card>
        </button>
    );
};
